/**
 * AUTOMATION FORM — Rule builder (da mockup crm-mockup.html, schermata id="builder").
 *
 * Stato del wiring (TS-65): testata, blocco <b>QUANDO</b> e blocco <b>SE</b> sono cablati su stato
 * locale — QUANDO legge il catalogo da `fetchAutomationObjectTypes` (oggetto → eventi → parametri
 * renderizzati per `ParamType`), SE da `fetchAutomationConditionTypes` (campo → operatore →
 * sorgente → valore). Anche il blocco <b>ALLORA</b> è cablato: `fetchAutomationActionTypes` porta
 * le azioni offribili per l'entità scelta nel QUANDO — il server le filtra per applicabilità — e la
 * modale ne compone titolo e politica di errore. Manca la sola UI di configurazione specifica di
 * ciascuna azione, che arriverà da un registry codice → componente.
 * <p>Il pannello <b>Validazione</b> è invece cablato sul backend: `validateAutomation` chiama
 * POST /automations/{groupId}/validate a ogni modifica (debounce). Le regole NON sono qui: il
 * pannello chiede, non giudica.
 * <p>Da quel verde, PIÙ l'assenza di modifiche pendenti (`isDirty`), dipende l'abilitazione di
 * <b>Attiva</b> e <b>Simula</b>. Entrambe agiscono sul RECORD IN DB, mentre /validate giudica il
 * payload a video: senza il secondo pezzo si potrebbe pubblicare — o simulare — una configurazione
 * diversa da quella guardata, e per giunta mai validata. Le pendenze si misurano confrontando il
 * payload serializzato con lo snapshot dell'ultimo momento allineato (fine idratazione o
 * salvataggio riuscito).
 * <p>Lo stesso form servirà creazione ed edit.
 *
 * ROUTE (TS-65) — lo STESSO componente serve 5 rotte, il modo si deriva dalla path (non da un flag
 * idratato). `isTemplateRoute` = path sotto /templates/; `isEdit` = path .../edit; read-only = id
 * senza /edit.
 * - `/automations/templates/new`      -> crea un TEMPLATE. <b>CABLATO</b>: "Salva bozza" fa
 *   `createAutomation` (nasce DRAFT) e naviga a /:id/edit.
 * - `/automations/templates/:id`      -> view SOLA LETTURA del template (qualunque stato).
 * - `/automations/templates/:id/edit` -> edit del template (solo DRAFT).
 * - `/automations/:id`                -> view SOLA LETTURA dell'automazione (qualunque stato).
 * - `/automations/:id/edit`           -> edit dell'automazione. <b>CABLATO</b>: "Salva bozza" fa
 *   `updateAutomation` (PATCH) e resta in pagina.
 * "Salva bozza" si abilita sui 4 campi minimi pretesi dalla guardia del salvataggio (nome, oggetto,
 * evento, categoria), non sul verde del pannello: una bozza a metà è salvabile (service.validate
 * "protegge il dato"). Solo le DRAFT sono salvabili; in edit un record non-DRAFT reindirizza alla
 * view read-only dello stesso record.
 * <p>Read-only (`isReadOnly` = id senza /edit): il corpo è un `<fieldset disabled>` (disabilita in
 * blocco input/chip/rimozioni) e gli "add" del SE/ALLORA sono nascosti. Unica cosa che resta
 * ATTIVA: la riga di un'azione, che apre la modale in consultazione — la configurazione non si vede
 * altrimenti, e la riga da sola dice titolo, tipo e politica di errore ma non il contenuto. Per
 * sfuggire al fieldset quel corpo non è un `<button>` ma un elemento con `role="button"`, cfr.
 * `ActionRowView`. L'action bar mostra
 * "Modifica" (solo se DRAFT → /edit) al posto di Salva/Attiva, più "Elimina" sui soli TEMPLATE in
 * bozza (la gallery non ha azioni di riga: è l'unico punto da cui si cancella un template).
 * NB (WIP): l'azione "Usa template" (crea automazione da un template + redirect a /:id/edit) e la
 * simmetria /simulation·/monitoring sono le fette successive.
 * <p>IDRATAZIONE (con id): <b>testata + QUANDO + SE</b>, in due fasi. Fase 1 — `fetchAutomation`
 * popola testata (nome/descrizione/categoria/status/versione) e QUANDO (oggetto/evento/eventConfig,
 * via `eventConfigFromDto`), una volta per record. Fase 2 — il blocco SE (`conditionGroupsFromRecord`)
 * attende il catalogo condizioni del className (serve la cardinalità dell'operatore per decodificare i
 * valori) e poi idrata i gruppi. "Attiva" resta publish-only (fetta pubblicazione).
 *
 * Layout come la pagina preventivi (QuotationForm): flex a due colonne con
 * `flex-1` a sinistra e `.summary-sidebar` a destra.
 * - colonna sinistra: back link, box titolo,
 *   e i 3 box separati QUANDO / SE / ALLORA (bordo + header keyword colorata +
 *   hint + body con chip/condizioni/azioni);
 * - colonna destra: la .summary-sidebar riusata, con i box Riepilogo e
 *   Validazione + action bar.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useIsClassNameDisabled } from '../../plugin/useCoreItems';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
// Glifi del design system per le azioni della colonna destra: nel concept ogni
// pulsante porta la sua icona.
import {
  CheckGlyph,
  AlertGlyph,
  EditGlyph,
  LayersGlyph,
  PauseGlyph,
  PlayGlyph,
  TrashGlyph,
} from '../../components/icons';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { CatalogValueField, ChipSelect } from './catalogFieldEditors';
import type { ChipOption } from './catalogFieldEditors';
import { useToast } from '../../components/useToast';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useConfirmDialog } from '../../components/useConfirmDialog';
import {
  actionConfigFromDto,
  actionConfigToDto,
  conditionValueFromDto,
  conditionValueToDto,
  createPayloadFromTemplate,
  eventConfigFromDto,
  eventConfigToDto,
} from './automationMappers';
import { SUMMARY_PLACEHOLDER, summarizeAutomation } from './automationSummary';
import AutomationSummaryTokens from './AutomationSummaryTokens';
import { clearAutomationViolations, clearCurrentAutomation } from './automationSlice';
import {
  createAutomation,
  createAutomationVersion,
  deleteAutomation,
  fetchAutomation,
  fetchAutomationActionTypes,
  fetchAutomationCategories,
  fetchAutomationConditionTypes,
  fetchAutomationObjectTypes,
  publishAutomation,
  resumeAutomation,
  setAutomationActionEnabled,
  setAutomationLogEveryRecord,
  suspendAutomation,
  updateAutomation,
  validateAutomation,
} from './automationThunks';
import { STATUS_CHIP_COLOR } from './automationTypes';
import type {
  AutomationActionInput,
  AutomationActionOnError,
  AutomationBlock,
  AutomationConditionGroup,
  AutomationConditionGroupInput,
  AutomationCreatePayload,
  AutomationStatus,
  CatalogField,
  EditableValueSource,
  OperatorCardinality,
} from './automationTypes';
import { actionIcon, DEFAULT_ACTION_ICON } from './automationIcons';
import AutomationActionModal, { type AutomationActionDraft } from './AutomationActionModal';
import ParamField from './ParamField';

/**
 * Pausa di quiete prima di rivalidare lato server. Abbastanza lunga da non chiamare a ogni battuta,
 * abbastanza corta da non far sembrare il pannello "in ritardo" mentre si compone.
 */
const VALIDATION_DEBOUNCE_MS = 500;

/**
 * Nome fittizio spedito a `/validate` quando l'utente non l'ha ancora scritto. L'endpoint condivide
 * il payload della create, dove `name` è `required` (bean validation → 400), ma NON lo guarda: la
 * testata la valida la guardia di salvataggio, non il pannello. Il segnaposto serve solo a superare
 * il vincolo di trasporto, e non finisce mai in database — `/validate` non persiste nulla.
 */
const VALIDATION_PLACEHOLDER_NAME = '—';

/** Una condizione del blocco SE (stato locale editor): campo + operatore + sorgente + valore
 *  (stringa, serializzata in AutomationCondition.valueJson al salvataggio, ancora deferito).
 *  L'`id` è locale e stabile per la key React (add/remove riordina l'array). */
interface ConditionRow {
  id: number;
  fieldName: string;
  operator: string;
  /** Come leggere `value`: 'STATIC' = letterale digitato; 'FIELD' = `fieldName` del campo destro. */
  valueSource: EditableValueSource;
  value: string;
}

/**
 * Riga del blocco ALLORA (rende {@link AutomationAction}): `code` scelto dal catalogo e `title`
 * composto nella modale, che lo pre-riempie con l'etichetta dell'azione. Entrambi arrivano da lì —
 * la riga non si compila a video, si apre.
 *
 * `id` locale e stabile per la key React, che l'indice non può essere: le frecce riordinano
 * l'array, e con l'indice come key React riuserebbe il DOM sbagliato.
 *
 * `automationActionId` è invece l'identità di PERSISTENZA: `> 0` per un'azione idratata da un
 * record (il backend la aggiorna in place preservando il suo log di esecuzioni), `0` per una nuova
 * aggiunta in edit. Distinto da `id` (locale, per React): due azioni nuove hanno `automationActionId`
 * 0 entrambe ma `id` diversi. Non è in {@link AutomationActionDraft} perché la modale non lo tocca —
 * è identità, non un campo che l'utente compone.
 *
 * Niente `priority`: la porta l'ordine dell'array, e si deriva dall'indice quando si serializza.
 * Tenerla anche qui vorrebbe dire due verità sullo stesso fatto, da risincronizzare a ogni mossa.
 *
 * `enabled` sta qui e non in {@link AutomationActionDraft} per la stessa ragione di
 * `automationActionId`: la modale non lo tocca. Non c'è ancora un comando per spegnere un'azione
 * senza cancellarla — ci sarà — ma il valore deve comunque **attraversare** l'editor: il motore
 * esegue le sole azioni abilitate, e un flag che non sopravvive al round-trip verrebbe riscritto a
 * `true` dal primo salvataggio, rimettendo in funzione un'azione spenta di proposito.
 */
interface ActionRow extends AutomationActionDraft {
  id: number;
  automationActionId: number;
  enabled: boolean;
}

/** Gruppo di condizioni del blocco SE (rende {@link AutomationConditionGroup}): condizioni in AND
 *  al suo interno, gruppi in OR tra loro. `id` locale per la key React. */
interface ConditionGroup {
  id: number;
  conditions: ConditionRow[];
}

/**
 * Stato dell'editor SE → `conditionGroups` del payload REST (lo stesso della create). Il valore lo
 * serializza `conditionValueToDto`, che conosce la grammatica del valore configurato; qui si mette insieme il
 * resto. Il `fieldType` arriva dal CATALOGO, non dall'editor: l'autorità sul tipo di un campo è il
 * provider, non ciò che il client si ricorda.
 * <p>Le righe incomplete non vengono filtrate: il payload racconta lo stato reale del builder, e sarà
 * il backend a dire cosa manca.
 */
function conditionGroupsToDto(
  groups: ConditionGroup[],
  fields: CatalogField[],
): AutomationConditionGroupInput[] {
  return groups.map((group, groupIndex) => ({
    // Dentro il gruppo le condizioni sono in AND; i gruppi fra loro in OR. Struttura fissa (DNF),
    // quindi nessun operatore di gruppo da serializzare.
    priority: groupIndex + 1,
    conditions: group.conditions.map((condition, index) => {
      const field = fields.find((f) => f.fieldName === condition.fieldName);
      const cardinality =
        field?.operators?.find((op) => op.code === condition.operator)?.cardinality ?? 'SINGLE';

      return {
        fieldName: condition.fieldName,
        fieldType: field?.type ?? 'STRING',
        operator: condition.operator,
        valueSource: condition.valueSource,
        value: field
          ? conditionValueToDto(field.type, cardinality, condition.valueSource, condition.value)
          : null,
        priority: index + 1,
      };
    }),
  }));
}

/**
 * Stato dell'editor ALLORA → `actions` del payload REST. Ogni riga porta già i campi che la modale
 * compone (code, titolo, politica di errore, retry) e l'`automationActionId` (identità di
 * persistenza: `> 0` idratata, `0` nuova — il backend allinea per-diff su questo). Qui si aggiungono
 * i tre che l'editor non chiede:
 * <ul>
 *   <li>`priority` = posizione + 1: l'ORDINE dell'array è la sequenza di esecuzione, e si materializza
 *       in `priority` solo qui, al confine col DTO. È il motivo per cui `ActionRow` non la porta —
 *       una sola verità, l'indice, e la sua proiezione al momento di serializzare (come per il SE).
 *   <li>`config` = la configurazione composta dal form per-azione, portata a stringa JSON da
 *       `actionConfigToDto`. Qui NON si guarda dentro e non si tipizza nulla: la config è opaca, e i
 *       valori arrivano già tipizzati dal form — l'unico che conosca il `type` del campo scelto. È
 *       la ragione per cui aggiungere un'azione non tocca questa funzione. Vale anche per le azioni
 *       SENZA parametri (`hasConfiguration: false`), che partono con `null`: è il server a salvarle
 *       come oggetto vuoto. Deciderlo qui legherebbe il payload al catalogo azioni, che può
 *       arrivare DOPO lo snapshot di idratazione (la fase 2 aspetta solo quello delle condizioni):
 *       il payload cambierebbe da solo e il form risulterebbe modificato senza che nessuno l'abbia
 *       toccato.
 *   <li>`enabled` = quello della riga, non un `true` cablato: il motore esegue le sole azioni
 *       abilitate, quindi riscriverlo qui riaccenderebbe in silenzio un'azione spenta. Il comando
 *       per spegnerla non c'è ancora, ma il valore fa comunque il giro completo — record → riga →
 *       payload — così quando arriverà sarà solo presentazione.
 * </ul>
 */
function actionsToDto(actions: ActionRow[]): AutomationActionInput[] {
  return actions.map((action, index) => ({
    automationActionId: action.automationActionId,
    code: action.code,
    title: action.title,
    config: actionConfigToDto(action.config),
    onError: action.onError,
    retryCount: action.retryCount,
    retryDelaySeconds: action.retryDelaySeconds,
    priority: index + 1,
    enabled: action.enabled,
  }));
}

/**
 * Inverso di {@link conditionGroupsToDto}: `conditionGroups` del record → stato editor del blocco SE
 * (idratazione in edit). Il valore lo decodifica `conditionValueFromDto`, che ha bisogno della
 * CARDINALITÀ dell'operatore (SINGLE/RANGE/SET) per interpretare il valore configurato — nota solo al catalogo
 * (`fields`), da cui questa idratazione dipende. Gruppi e condizioni si ordinano per `priority` (è
 * l'ordine che l'encode ha scritto). `EXPRESSION`, che l'editor non rappresenta, ricade su STATIC.
 * Gli `id` sono locali e nuovi (contatori dell'editor), per le key React.
 */
function conditionGroupsFromRecord(
  recordGroups: AutomationConditionGroup[],
  fields: CatalogField[],
  nextGroupId: { current: number },
  nextConditionId: { current: number },
): ConditionGroup[] {
  return [...recordGroups]
    .sort((a, b) => a.priority - b.priority)
    .map((group) => ({
      id: nextGroupId.current++,
      conditions: [...group.conditions]
        .sort((a, b) => a.priority - b.priority)
        .map((condition) => {
          const field = fields.find((f) => f.fieldName === condition.fieldName);
          const cardinality =
            field?.operators?.find((op) => op.code === condition.operator)?.cardinality ?? 'SINGLE';
          const valueSource: EditableValueSource =
            condition.valueSource === 'FIELD' ? 'FIELD' : 'STATIC';

          return {
            id: nextConditionId.current++,
            fieldName: condition.fieldName,
            operator: condition.operator,
            valueSource,
            value: conditionValueFromDto(cardinality, valueSource, condition.value),
          };
        }),
    }));
}

/**
 * `candidate` è confrontabile con `field` come termine destro (sorgente FIELD)? Serve lo STESSO
 * ParamType — niente DATE contro STRING — e un campo diverso da sé stesso.
 * <p>Sugli ENUM il vincolo si ferma al tipo: due campi enumerati sono ammessi anche con domini
 * diversi (es. stato lead contro stato marketing). Scelta esplicita — il tipo non basta a garantire
 * che il confronto abbia senso, ma la valutazione è del motore, non dell'editor.
 */
function isComparableTo(field: CatalogField, candidate: CatalogField): boolean {
  return candidate.fieldName !== field.fieldName && candidate.type === field.type;
}

/**
 * Riga condizione del blocco SE: combo campo → combo operatore (dal `type` del campo) → combo
 * sorgente → editor valore. La sorgente ({@link EditableValueSource}) decide COSA sta a destra:
 * 'STATIC' un valore digitato ({@link CatalogValueField}), 'FIELD' un altro campo del record
 * (tendina dei campi compatibili). Il prefisso logico "E" compare dalla seconda riga (gruppo in
 * AND; OR-gruppi rinviati).
 */
function ConditionRowEditor({
  fields,
  condition,
  showLogic,
  onChange,
  onRemove,
}: {
  fields: CatalogField[];
  condition: ConditionRow;
  showLogic: boolean;
  onChange: (next: ConditionRow) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const field = fields.find((f) => f.fieldName === condition.fieldName);

  const fieldOptions = fields
    .map((f) => ({ value: f.fieldName, label: f.label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const operatorOptions = (field?.operators ?? []).map((op) => ({
    value: op.code,
    label: op.label,
  }));
  // Cardinalità dell'operatore scelto: decide la forma dell'editor di valore (uno/coppia/insieme).
  const selectedOperator = field?.operators?.find((op) => op.code === condition.operator);
  const cardinality: OperatorCardinality = selectedOperator?.cardinality ?? 'SINGLE';

  // Termine destro con sorgente FIELD: solo i campi confrontabili col campo scelto.
  const compareFieldOptions = (field ? fields.filter((f) => isComparableTo(field, f)) : [])
    .map((f) => ({ value: f.fieldName, label: f.label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // FIELD regge solo con operatori a valore singolo (su BETWEEN/IN/relativi il termine destro è una
  // coppia, un insieme o una quantità: resta statico) e solo se esiste almeno un campo compatibile
  // da confrontare.
  const canUseField = cardinality === 'SINGLE' && compareFieldOptions.length > 0;

  const valueSourceOptions = [
    { value: 'STATIC', label: t('automation.form.builder.valueSourceStatic') },
    { value: 'FIELD', label: t('automation.form.builder.valueSourceField') },
  ];

  // Valore di partenza dello slot destro: un BOOLEAN statico nasce 'false' (lo Switch off deve
  // corrispondere a un valore esplicito, non a stringa vuota); ogni altro caso riparte vuoto.
  const initialValue = (f: CatalogField | undefined, source: EditableValueSource) =>
    source === 'STATIC' && f?.type === 'BOOLEAN' ? 'false' : '';

  // Cambio campo: azzera operatore (default primo) e valore (schema di editor diverso). La sorgente
  // FIELD sopravvive solo se regge anche col nuovo campo (operatore SINGLE + campi compatibili).
  const handleField = (fieldName: string) => {
    const f = fields.find((x) => x.fieldName === fieldName);
    const nextCardinality = f?.operators?.[0]?.cardinality ?? 'SINGLE';
    const hasCompareField = !!f && fields.some((x) => isComparableTo(f, x));
    const nextSource: EditableValueSource =
      condition.valueSource === 'FIELD' && nextCardinality === 'SINGLE' && hasCompareField
        ? 'FIELD'
        : 'STATIC';
    onChange({
      ...condition,
      fieldName,
      operator: f?.operators?.[0]?.code ?? '',
      valueSource: nextSource,
      value: initialValue(f, nextSource),
    });
  };

  return (
    <div className="builder-crow">
      {showLogic && (
        <span className="builder-logic">{t('automation.form.builder.logicAnd')}</span>
      )}
      <ChipSelect
        value={condition.fieldName}
        options={fieldOptions}
        placeholder={t('automation.form.builder.selectField')}
        onChange={handleField}
      />
      {field && (
        <>
          <ChipSelect
            className="builder-op"
            value={condition.operator}
            options={operatorOptions}
            placeholder={t('automation.form.builder.selectOperator')}
            onChange={(operator) => {
              const nextCardinality =
                field.operators?.find((op) => op.code === operator)?.cardinality ?? 'SINGLE';
              // RANGE/SET/RELATIVE non ammettono FIELD: la sorgente ricade su STATIC.
              const nextSource: EditableValueSource =
                nextCardinality === 'SINGLE' ? condition.valueSource : 'STATIC';
              // Arità o sorgente cambiate → il valore ha un altro schema (scalare/coppia/insieme/
              // quantità/nome di campo): azzera.
              const sameShape =
                nextCardinality === cardinality && nextSource === condition.valueSource;
              onChange({
                ...condition,
                operator,
                valueSource: nextSource,
                value: sameShape ? condition.value : initialValue(field, nextSource),
              });
            }}
          />
          <ChipSelect
            className="builder-op"
            value={condition.valueSource}
            options={valueSourceOptions}
            disabled={!canUseField}
            onChange={(source) => {
              // Cambio sorgente → il valore corrente non ha più senso (letterale vs nome di campo).
              const nextSource = source as EditableValueSource;
              onChange({
                ...condition,
                valueSource: nextSource,
                value: initialValue(field, nextSource),
              });
            }}
          />
          {condition.valueSource === 'FIELD' ? (
            <ChipSelect
              className="builder-selchip builder-selchip--val"
              value={condition.value}
              options={compareFieldOptions}
              placeholder={t('automation.form.builder.selectCompareField')}
              onChange={(value) => onChange({ ...condition, value })}
            />
          ) : (
            <CatalogValueField
              field={field}
              cardinality={cardinality}
              value={condition.value}
              onChange={(value) => onChange({ ...condition, value })}
            />
          )}
        </>
      )}
      <button
        type="button"
        className="builder-crow__remove"
        aria-label={t('common.delete')}
        onClick={onRemove}
      >
        ✕
      </button>
    </div>
  );
}

/**
 * Riga del blocco ALLORA: numero d'ordine, icona, titolo, e i comandi per aprirla, spostarla o
 * toglierla.
 *
 * Il NUMERO non è decorativo: le azioni si eseguono in sequenza, e la posizione è l'unica cosa che
 * lo dice. Per la stessa ragione le frecce sono qui e non altrove — l'ordine è modificabile perché è
 * un contenuto dell'automazione.
 *
 * Il `title` viene dalla RIGA, non dal catalogo: è testo dell'utente (la modale lo pre-riempie con
 * l'etichetta, poi è suo). Dal catalogo vengono `icon` e `description`, e se il codice non c'è più
 * — plugin spento, azione ritirata — la riga resta comunque leggibile: il titolo è persistito e non
 * dipende dal catalogo, l'icona ricade sul token generico e la descrizione semplicemente non
 * compare.
 *
 * Il corpo apre la modale invece di essere un quarto bottone: le azioni della riga sono già tre, e
 * "apri" è l'intenzione principale, non una fra le altre.
 *
 * Il chip a destra mostra la POLITICA DI ERRORE (come il mockup): è una proprietà comune a ogni
 * azione, non parte della config specifica del tipo, quindi la riga può mostrarla anche senza
 * conoscere la configurazione.
 *
 * Sotto il titolo va la descrizione del TIPO ("Aggiorna il valore di un campo dell'oggetto"), che
 * dice di che azione si tratta quando il titolo scelto dall'utente non lo dice. Da non confondere
 * col DETTAGLIO della singola configurazione ("Porta Stato lead a Qualificato"), che ancora non
 * c'è: quello dovrebbe leggere la config, e a saperla leggere è solo il provider — sarebbe una
 * descrizione calcolata per ogni azione salvata, non un campo del catalogo.
 */
function ActionRowView({
  index,
  total,
  title,
  description,
  icon,
  onError,
  enabled,
  busy,
  readOnly,
  onEdit,
  onMove,
  onRemove,
  onToggle,
}: {
  index: number;
  total: number;
  title: string;
  /**
   * Descrizione del TIPO di azione, dal catalogo. Facoltativa — il server la omette per le azioni
   * che non la dichiarano — e assente anche quando il catalogo non è ancora arrivato.
   */
  description?: string;
  icon: string;
  onError: AutomationActionOnError;
  /**
   * Azione spenta = configurata ma non eseguita: il motore applica le sole abilitate. Lo stato si
   * vede SEMPRE, anche in sola lettura, perché è ciò che spiega perché un'automazione a video
   * completa non fa quella cosa; il comando per cambiarlo, invece, esiste solo in modifica.
   */
  enabled: boolean;
  /**
   * Scrittura in corso sul record: l'interruttore resta a video ma non risponde. Vale solo in sola
   * lettura, dove il clic va al server; in modifica il flag cambia in locale e non c'è attesa da
   * dichiarare.
   */
  busy: boolean;
  readOnly: boolean;
  onEdit: () => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const Icon = actionIcon(icon);
  // Chip col PREFISSO ("on error: interrompi"), non le label brevi della select: sul chip il
  // contesto va detto, mentre nella select lo dà l'etichetta del campo. Le chiavi `builder.onError*`
  // sono per-valore e minuscole, quindi si derivano dal nome dell'enum senza una mappa a parte.
  const onErrorLabel = `automation.form.builder.onError${onError.charAt(0)}${onError.slice(1).toLowerCase()}`;
  // Una stringa sola per tooltip e aria-label: dicono la stessa cosa a due pubblici diversi, e
  // tenerle separate le farebbe divergere alla prima modifica. Descrive l'AZIONE DEL CLIC e non lo
  // stato corrente — quello lo dice già l'icona — perché è la domanda di chi ci sta sopra col
  // puntatore: "se premo, cosa succede?".
  const toggleLabel = t(
    enabled ? 'automation.form.builder.disableAction' : 'automation.form.builder.enableAction');

  return (
    <div className={`builder-arow${enabled ? '' : ' builder-arow--off'}`}>
      <span className="builder-arow__num">{index + 1}</span>
      {/* La variante del riquadro segue il token: se lo SCSS non ha una regola per quel token, resta
          lo stile di base. Il colore degrada da sé, senza bisogno di una seconda mappa qui. */}
      <span className={`builder-arow__icon builder-arow__icon--${icon}`}>
        <Icon />
      </span>
      {/* NON è un <button>, ed è una scelta obbligata: il corpo del builder è un
          `<fieldset disabled>` in sola lettura, e un fieldset disabilitato spegne ogni controllo di
          form al suo interno — compresi i bottoni. La riga però deve restare apribile anche in
          consultazione, perché è l'unico modo per vedere la configurazione di un'azione. Un
          elemento non-form sfugge al fieldset, come già fanno gli `<a>` di "aggiungi".
          Il prezzo è la semantica da ricostruire a mano: `role`, `tabIndex` e la tastiera. */}
      <div
        role="button"
        tabIndex={0}
        className="builder-arow__body builder-arow__open"
        onClick={onEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onEdit();
          }
        }}
      >
        <span className="builder-arow__title">{title}</span>
        {/* Il titolo è testo dell'utente e può non dire che TIPO di azione sia ("Qualifica il
            lead"): la descrizione del catalogo lo chiarisce, e serve soprattutto in sola lettura,
            dove la modale non si apre per andarlo a scoprire. Resa solo se c'è: è facoltativa sul
            contratto, e finché il catalogo è in volo non c'è comunque. */}
        {description && <span className="builder-arow__detail">{description}</span>}
      </div>
      <span className="builder-onerr">{t(onErrorLabel)}</span>
      {/* Interruttore, non una spunta dentro la modale: spegnere un'azione è un ripensamento
          sull'automazione — "questa per ora no" — non un dettaglio della sua configurazione, e lo
          si fa guardando l'elenco. Con la spunta si sarebbe dovuta aprire una riga per volta per
          sapere quali fossero attive.
          FUORI dal blocco `!readOnly`, a differenza di sposta ed elimina: spegnere un'azione si
          deve poter fare anche su un'automazione ATTIVA, che l'editor non apre. È la leva per
          fermare in fretta un effetto indesiderato senza passare da una nuova versione.
          `div role="button"` e non `<button>` per la stessa ragione del corpo riga qui sopra: in
          sola lettura il builder è un `<fieldset disabled>`, che spegnerebbe un controllo di form —
          e proprio dove questo comando serve di più.
          `aria-pressed` e non un checkbox: è un interruttore a due stati sullo stesso oggetto, e
          l'etichetta cambia con lo stato per dire cosa FARÀ il clic. */}
      <Tooltip title={toggleLabel} placement="top" arrow>
        <div
          role="button"
          tabIndex={0}
          className="builder-arow__toggle"
          aria-label={toggleLabel}
          aria-pressed={enabled}
          aria-disabled={busy}
          onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle();
            }
          }}
        >
          {enabled ? '◉' : '○'}
        </div>
      </Tooltip>
      {!readOnly && (
        <>
          <button
            type="button"
            className="builder-arow__move"
            aria-label={t('common.moveUp')}
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            ↑
          </button>
          <button
            type="button"
            className="builder-arow__move"
            aria-label={t('common.moveDown')}
            disabled={index === total - 1}
            onClick={() => onMove(1)}
          >
            ↓
          </button>
          <button
            type="button"
            className="builder-crow__remove"
            aria-label={t('common.delete')}
            onClick={onRemove}
          >
            ✕
          </button>
        </>
      )}
    </div>
  );
}

export default function AutomationForm() {
  const { t } = useTranslation();
  // id del template/automazione dalla rotta (/templates/:id o /:id/edit); assente su /templates/new.
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Modo del builder DALLA ROUTE (non più da un flag idratato dal record):
  // - /automations/templates/new      → crea un TEMPLATE (id assente);
  // - /automations/templates/:id      → view SOLA LETTURA del template;
  // - /automations/templates/:id/edit → edit del template;
  // - /automations/:id                → view SOLA LETTURA dell'automazione;
  // - /automations/:id/edit           → edit dell'automazione.
  // `isTemplateRoute` viene dalla PATH (sotto /templates/), non dal record: l'URL è la fonte di
  // verità, così la pagina è parlante e non deve indovinare template-vs-automazione da uno stato
  // idratato.
  const isTemplateRoute = location.pathname.startsWith('/automations/templates/');
  const isNewTemplate = !id; // l'unica rotta del form senza id è /templates/new
  const isEdit = location.pathname.endsWith('/edit');
  // View sola lettura: id presente ma NON in edit (né /new). Governa il corpo (`fieldset disabled`),
  // gli "add" del SE/ALLORA e quali azioni mostra la barra. NON governa la validazione: l'effetto che
  // chiama /validate dipende dal solo `isValidatable`, quindi il pannello si pronuncia anche qui —
  // per scelta, cfr. il commento del pannello Validazione più sotto.
  const isReadOnly = Boolean(id) && !isEdit;

  // Rotte della pagina, derivate in un posto solo: ogni navigazione sceglie fra il namespace template
  // e quello automazione, e finché la scelta si riscriveva a mano a ogni call site bastava
  // dimenticarne uno — è successo nel ramo di creazione, che mandava un template appena creato
  // sull'editor delle automazioni.
  // La funzione prende l'id perché non tutte le navigazioni vanno al record DI ROTTA: la creazione va
  // su quello appena nato, il cui id arriva dalla risposta. `recordPath` è il caso comune, cioè
  // questo record; con `id` assente (/templates/new) non ha significato ma nemmeno lettori.
  const pathForRecord = (recordId: string | number) =>
    isTemplateRoute ? `/automations/templates/${recordId}` : `/automations/${recordId}`;
  const recordPath = pathForRecord(id ?? '');
  const recordEditPath = `${recordPath}/edit`;
  // Elenco di provenienza, dove si torna dopo aver cancellato: la gallery dei template o l'elenco
  // delle automazioni.
  const listPath = isTemplateRoute ? '/automations/templates' : '/automations';

  // ─── Testata — campi di AutomationDefinition (name/description) + stato/versione ───
  // Stesso form per creazione ED edit: in edit sono idratati dal record caricato (currentItem, effetto
  // di idratazione più sotto). `status`/`version` sono read-only a video (badge): DRAFT/v1 in
  // creazione, il valore del record in modifica.
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Categoria dell'automazione: obbligatoria per il SALVATAGGIO (il service pretende categoryId > 0,
  // cfr. AutomationDefinitionLocalServiceImpl.validate). 0 = nessuna scelta. Il pannello /validate la
  // ignora, ma la create la esige: senza categoria "Salva bozza" resta spento.
  const [automationCategoryId, setAutomationCategoryId] = useState(0);
  const categories = useAppSelector((s) => s.automations.categories);
  // Categoria scelta: porta i colori del chip (backColor/fontColor). Assente = chip neutro (placeholder).
  const selectedCategory = categories.find((c) => c.automationCategoryId === automationCategoryId);
  const [status, setStatus] = useState<AutomationStatus>('draft');
  const [version, setVersion] = useState(1);

  // ─── Blocco QUANDO — stato locale (solo editor; salvataggio deferito, TS-65) ───
  const dispatch = useAppDispatch();
  const toast = useToast();
  // Conferma per l'unica azione distruttiva della pagina (elimina template). Stessa istanza riusabile
  // dell'elenco: il dialog è montato una volta in fondo al render.
  const confirm = useConfirmDialog();
  const objectTypes = useAppSelector((s) => s.automations.objectTypes);
  // Un tipo spento dalla pagina «Voci del CRM» (FILO-53) non si offre come oggetto.
  const isClassNameDisabled = useIsClassNameDisabled();
  // Record da idratare in edit (/:id/edit): lo carica l'effetto di load qui sotto. Null in creazione.
  const currentItem = useAppSelector((s) => s.automations.currentItem);

  // className (FQN dell'oggetto), eventCode (AutomationDefinition.eventType) e i valori dei
  // parametri dell'evento (→ eventConfig). Stato locale: nessun submit reale ancora collegato.
  const [className, setClassName] = useState('');
  const [eventCode, setEventCode] = useState('');
  const [eventConfig, setEventConfig] = useState<Record<string, unknown>>({});
  // Riscatto: riguarda i soli eventi schedulati, e il campo si mostra solo per quelli. Lo stato
  // resta comunque qui e non dentro eventConfig — non è un parametro dell'evento ma una scelta
  // sull'automazione, e ha una colonna propria.
  const [repeatable, setRepeatable] = useState(false);

  // Quanto storico tenere. Nasce SPENTO come il record che sta per essere creato: il log ridotto è
  // il default del dominio, e un'automazione nuova non deve partire scrivendo una riga per ogni
  // record che guarda. In edit lo sovrascrive il valore del record, come per ogni altro campo.
  const [logEveryRecord, setLogEveryRecord] = useState(false);

  // Catalogo ALLORA: alimenta la modale di scelta azione e il riepilogo, che di un'azione persiste
  // il codice e deve nominarla. Per ENTITÀ come il SE — il server offre le sole azioni applicabili
  // — quindi indicizzato per className e caricato al cambio oggetto, non al mount.
  const actionTypesByClassName = useAppSelector((s) => s.automations.actionTypesByClassName);
  const actionTypes = className ? (actionTypesByClassName[className] ?? []) : [];

  // ─── Blocco SE — stato locale (editor; salvataggio deferito, TS-65) ───
  // I campi condizione dipendono dall'oggetto scelto nel QUANDO: catalogo lazy per className.
  const conditionTypesByClassName = useAppSelector((s) => s.automations.conditionTypesByClassName);
  // useMemo per identità stabile: conditionFields è dep di una useMemo più sotto; senza
  // memoizzazione il `?? []` creerebbe un nuovo array a ogni render (loop di ricalcolo).
  const conditionFields = useMemo(
    () => (className ? (conditionTypesByClassName[className] ?? []) : []),
    [className, conditionTypesByClassName],
  );
  // Gruppi in OR tra loro; dentro il gruppo le condizioni sono in AND. Si parte da un gruppo vuoto.
  const [groups, setGroups] = useState<ConditionGroup[]>([{ id: 0, conditions: [] }]);
  const nextGroupIdRef = useRef(1);
  const nextConditionIdRef = useRef(1);

  // ─── Blocco ALLORA — stato locale (editor; serializzazione e idratazione deferite, TS-65) ───
  // Le azioni si eseguono in SEQUENZA, quindi l'ordine dell'array è il contenuto, non una vista:
  // `priority` non sta qui, si deriva dall'indice al momento di serializzare. Una riga porta i campi
  // COMUNI a ogni azione (code, titolo, politica di errore, retry: li compone la modale); la config
  // SPECIFICA del tipo non c'è ancora — come il builder arrivi a quella UI è ancora da decidere (TS-65).
  const [actions, setActions] = useState<ActionRow[]>([]);
  const nextActionIdRef = useRef(1);

  // Textarea descrizione (per l'auto-grow, che va ricalcolato anche sul valore idratato).
  const descRef = useRef<HTMLTextAreaElement>(null);
  // Id già idratato: l'idratazione popola il form UNA volta per record, senza sovrascrivere le
  // modifiche dell'utente ai render successivi (né ri-idratare se il record cambia identità).
  const hydratedIdRef = useRef<number | null>(null);
  // Id già idratato per il blocco SE (fase 2, separata: attende il catalogo condizioni del className).
  const seHydratedIdRef = useRef<number | null>(null);
  // Record per cui l'idratazione è CONCLUSA (fasi 1 e 2). Stato e non ref, a differenza dei due
  // sopra: da qui dipende lo snapshot del payload, che va preso in un render dove i campi idratati
  // sono già applicati. Un ref non innescherebbe quel render, e lo snapshot uscirebbe vuoto.
  const [hydratedRecordId, setHydratedRecordId] = useState<number | null>(null);
  // Payload serializzato nell'ultimo istante in cui form e record COINCIDEVANO: fine idratazione, o
  // salvataggio riuscito. `null` = non ancora noto, che vale come "disallineato" — è lo stato in cui
  // si nasce e quello in cui un salvataggio rimette le cose finché lo snapshot non viene ripreso.
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);

  // Cataloghi del sito caricati una volta al mount (object-types del QUANDO e categorie della
  // testata, entrambi per-groupId). Si azzera anche l'esito della validazione: `violations` vive
  // nello slice, quindi una bozza nuova erediterebbe l'esito di quella appena chiusa — e con una
  // lista vuota ereditata "Attiva" sarebbe acceso su un form vuoto.
  useEffect(() => {
    dispatch(clearAutomationViolations());
    dispatch(fetchAutomationObjectTypes());
    dispatch(fetchAutomationCategories());
  }, [dispatch]);

  // Edit: carica il record dell'id di rotta (una volta) e lo pulisce all'uscita, così una bozza
  // appena chiusa non idrata la successiva. Su /templates/new (id assente) non carica nulla.
  useEffect(() => {
    // Cambiando id (es. dopo la create, che passa da /new a /:id/edit) lo snapshot del record
    // precedente non vale più: si riparte da "non noto" e lo riprende l'effetto di allineamento,
    // a idratazione conclusa. Senza questo, il confronto girerebbe contro il payload di un altro
    // record e i pulsanti resterebbero spenti per sempre.
    setSavedSnapshot(null);
    if (id) dispatch(fetchAutomation(Number(id)));
    return () => {
      dispatch(clearCurrentAutomation());
    };
  }, [dispatch, id]);

  // Guardia di accesso all'edit, in due priorità (appena il record è caricato):
  //  1) NAMESPACE COERENTE — `isTemplateRoute` viene dalla rotta, ma il service scrive il flag
  //     `template` dal payload: aprire un record nel namespace sbagliato (automazione su
  //     /templates/:id/edit o viceversa) e salvare lo convertirebbe. Se rotta e record non combaciano,
  //     si va all'edit del namespace GIUSTO (auto-correzione), così `isTemplateRoute` è sempre
  //     allineato prima di un submit.
  //  2) SOLO DRAFT — un record non-bozza non si modifica: avviso + view SOLA LETTURA dello stesso
  //     record (namespace ormai coerente). `replace` così il back non riporta sull'edit vietato.
  useEffect(() => {
    if (!isEdit || !currentItem) return;
    if (currentItem.automationDefinitionId !== Number(id)) return;

    // Unica navigazione della pagina che NON usa `recordEditPath`, ed è il punto: quella costante
    // deriva dalla rotta, mentre qui si va deliberatamente dove dice il RECORD — è la rotta a essere
    // sbagliata, ed è ciò che si sta correggendo.
    if (currentItem.template !== isTemplateRoute) {
      navigate(
        currentItem.template
          ? `/automations/templates/${id}/edit`
          : `/automations/${id}/edit`,
        { replace: true },
      );
      return;
    }
    if (currentItem.status !== 'draft') {
      toast({ severity: 'warning', message: t('automation.form.onlyDraftEditable') });
      navigate(recordPath, { replace: true });
    }
  }, [isEdit, isTemplateRoute, recordPath, id, currentItem, toast, navigate, t]);

  // Idratazione — FASE 1 (testata + QUANDO). Scatta quando il record dell'id di rotta è in
  // `currentItem`, una sola volta per record (hydratedIdRef). Il blocco SE è la FASE 2 (sotto): dipende
  // dal catalogo condizioni del className, che l'effetto più giù carica appena `setClassName` lo imposta.
  useEffect(() => {
    if (!id || !currentItem) return;
    if (currentItem.automationDefinitionId !== Number(id)) return; // record di un altro id, in arrivo
    // In edit non si idrata se la guardia sopra sta per reindirizzare (namespace incoerente o non-DRAFT):
    // così il form non lampeggia popolato un istante prima di uscire, né idrata sulla rotta sbagliata.
    if (isEdit && (currentItem.template !== isTemplateRoute || currentItem.status !== 'draft')) return;
    if (hydratedIdRef.current === currentItem.automationDefinitionId) return; // già idratato
    hydratedIdRef.current = currentItem.automationDefinitionId;

    // Testata
    setName(currentItem.name);
    setDescription(currentItem.description ?? '');
    setAutomationCategoryId(currentItem.automationCategoryId);
    setStatus(currentItem.status);
    setVersion(currentItem.version);
    // QUANDO — setClassName innesca il fetch del catalogo condizioni (usato dalla fase 2, il SE).
    setClassName(currentItem.className);
    setEventCode(currentItem.eventType);
    setEventConfig(eventConfigFromDto(currentItem.eventConfig));
    setRepeatable(currentItem.repeatable);
    setLogEveryRecord(currentItem.logEveryRecord);
    // ALLORA — le azioni si idratano già qui (fase 1) e non nella 2 come il SE: non serve alcun
    // catalogo per ricostruirle (l'icona la risolve il render), quindi non c'è nulla da attendere.
    // `automationActionId` dal record = identità di persistenza per il per-diff al salvataggio; `id`
    // è locale (React key). Le righe portano solo i campi che la modale poi modifica + l'identità.
    setActions(
      (currentItem.actions ?? []).map((action) => ({
        id: nextActionIdRef.current++,
        automationActionId: action.automationActionId,
        code: action.code,
        title: action.title,
        // La config del record è una STRINGA JSON, il form vuole l'oggetto: `actionConfigFromDto`
        // la decodifica senza guardarci dentro. Illeggibile o assente danno `null`, che per il form
        // è indistinguibile da un'azione mai configurata — ed è quello che è, dal suo punto di vista.
        config: actionConfigFromDto(action.config),
        onError: action.onError,
        retryCount: action.retryCount,
        retryDelaySeconds: action.retryDelaySeconds,
        // Trasportato e non ricalcolato: l'editor non ha (ancora) un modo per cambiarlo, ma deve
        // restituirlo com'era o il salvataggio lo riporterebbe a `true`.
        enabled: action.enabled,
      })),
    );
  }, [id, currentItem, isEdit, isTemplateRoute]);

  // Idratazione — FASE 2 (blocco SE). Separata perché decodificare un valore di condizione richiede la
  // CARDINALITÀ dell'operatore, nota solo al catalogo condizioni del className: si attende che sia in
  // cache (lo carica l'effetto sotto, innescato dal setClassName della fase 1). Una volta per record.
  useEffect(() => {
    if (!id || !currentItem) return;
    if (currentItem.automationDefinitionId !== Number(id)) return;
    // Stessa guardia della fase 1: non idratare il SE su una rotta edit che sta per reindirizzare.
    if (isEdit && (currentItem.template !== isTemplateRoute || currentItem.status !== 'draft')) return;
    if (seHydratedIdRef.current === currentItem.automationDefinitionId) return;

    const recordGroups = currentItem.conditionGroups ?? [];
    // Nessuna condizione: niente da decodificare, resta il gruppo vuoto iniziale (nessun catalogo atteso).
    if (recordGroups.length === 0) {
      seHydratedIdRef.current = currentItem.automationDefinitionId;
      setHydratedRecordId(currentItem.automationDefinitionId);
      return;
    }
    // Con condizioni serve il catalogo del className: se non è ancora arrivato, si riprova al prossimo render.
    const fields = currentItem.className
      ? conditionTypesByClassName[currentItem.className]
      : undefined;
    if (!fields) return;

    seHydratedIdRef.current = currentItem.automationDefinitionId;
    setGroups(
      conditionGroupsFromRecord(recordGroups, fields, nextGroupIdRef, nextConditionIdRef),
    );
    setHydratedRecordId(currentItem.automationDefinitionId);
  }, [id, currentItem, conditionTypesByClassName, isEdit, isTemplateRoute]);

  // Auto-grow della descrizione: riadatta l'altezza a ogni cambio di `description`, così copre sia la
  // digitazione sia l'idratazione in edit (che imposta il valore senza passare da onChange).
  useEffect(() => {
    const el = descRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [description]);

  // Al cambio oggetto carica i cataloghi che dipendono dall'entità — campi condizione (SE) e azioni
  // offribili (ALLORA) — se non già in cache per quel className. Le azioni si caricavano al mount
  // quando il catalogo era globale: ora il server le filtra per applicabilità, e cambiare oggetto
  // può cambiare l'elenco.
  useEffect(() => {
    if (!className) return;

    if (!conditionTypesByClassName[className])
      dispatch(fetchAutomationConditionTypes(className));

    if (!actionTypesByClassName[className]) dispatch(fetchAutomationActionTypes(className));
  }, [className, conditionTypesByClassName, actionTypesByClassName, dispatch]);

  const emptyCondition = (): ConditionRow => ({
    id: nextConditionIdRef.current++,
    fieldName: '',
    operator: '',
    valueSource: 'STATIC',
    value: '',
  });
  const addGroup = () =>
    setGroups((prev) => [...prev, { id: nextGroupIdRef.current++, conditions: [] }]);
  const removeGroup = (groupId: number) =>
    setGroups((prev) => prev.filter((g) => g.id !== groupId));
  const addCondition = (groupId: number) =>
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId ? { ...g, conditions: [...g.conditions, emptyCondition()] } : g));
  const updateCondition = (groupId: number, conditionId: number, next: ConditionRow) =>
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? { ...g, conditions: g.conditions.map((c) => (c.id === conditionId ? next : c)) }
          : g));
  const removeCondition = (groupId: number, conditionId: number) =>
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? { ...g, conditions: g.conditions.filter((c) => c.id !== conditionId) }
          : g));

  // ALLORA. Aggiunta e modifica passano dalla stessa modale, quindi dallo stesso punto di rientro:
  // `editing` dice se è aperta e su cosa — `null` chiusa, `undefined` in aggiunta, una riga in
  // modifica. Un solo stato invece di un booleano più un id, che potrebbero contraddirsi.
  const [editing, setEditing] = useState<ActionRow | undefined | null>(null);
  const upsertAction = (draft: AutomationActionDraft) => {
    setActions((prev) =>
      editing
        ? // Modifica: lo spread preserva `id`, `automationActionId` ed `enabled` della riga, che il
          // draft non porta — la modale compone la configurazione, non l'identità né lo stato.
          prev.map((a) => (a.id === editing.id ? { ...a, ...draft } : a))
        : // Nuova: non ancora persistita, quindi `automationActionId` 0 — il backend la creerà — e
          // `enabled` acceso, perché un'azione la si aggiunge per farla agire.
          [...prev,
            { id: nextActionIdRef.current++, automationActionId: 0, enabled: true, ...draft }]);
    setEditing(null);
  };
  const removeAction = (actionId: number) =>
    setActions((prev) => prev.filter((a) => a.id !== actionId));
  // Spegne o riaccende: l'azione resta nell'automazione, con la sua configurazione e la sua
  // posizione, ma il motore non la esegue. È l'alternativa al cancella-e-ricomponi quando si vuole
  // sospendere un effetto senza perdere come era stato scritto — e a differenza della rimozione non
  // orfanizza il suo storico di esecuzioni.
  const toggleAction = (actionId: number) =>
    setActions((prev) =>
      prev.map((a) => (a.id === actionId ? { ...a, enabled: !a.enabled } : a)));
  // Scambio con la riga adiacente. Su una copia: mai mutare l'array di stato in place.
  const moveAction = (index: number, delta: number) =>
    setActions((prev) => {
      const to = index + delta;

      if (to < 0 || to >= prev.length) return prev;

      const next = [...prev];

      [next[index], next[to]] = [next[to], next[index]];

      return next;
    });

  // Derivazioni: oggetto scelto → eventi filtrati in memoria → evento scelto → parametri.
  const selectedObject = objectTypes.find((o) => o.className === className);
  const events = selectedObject?.events ?? [];
  const selectedEvent = events.find((e) => e.code === eventCode);
  const params = selectedEvent?.params ?? [];

  // Ordinamento alfabetico per etichetta localizzata: è responsabilità del frontend (che conosce
  // la locale), così il backend resta libero di restituire l'ordine dei provider. localeCompare
  // per un ordinamento corretto con accenti/maiuscole.
  const byLabel = (a: ChipOption, b: ChipOption) => a.label.localeCompare(b.label);
  const objectOptions = objectTypes
    .filter((o) => !isClassNameDisabled(o.className))
    .map((o) => ({ value: o.className, label: o.label }))
    .sort(byLabel);
  const eventOptions = events.map((e) => ({ value: e.code, label: e.label })).sort(byLabel);

  // Cambio oggetto: azzero evento, parametri, condizioni E azioni — tutto quanto sta a valle
  // dell'oggetto gli appartiene, e sopravvivergli significherebbe descrivere l'entità sbagliata.
  //
  // Le azioni si azzerano per DUE ragioni, e la seconda è quella che conta. La prima è
  // l'applicabilità: un'azione offerta sull'entità vecchia può non esserlo sulla nuova, e il
  // catalogo (per className) lo direbbe — ma lo direbbe in modo asincrono, quando la risposta
  // arriva, non qui. La seconda è la CONFIG: `crm.update-field` porta dentro `className` e
  // `fieldName` della vecchia entità, e un'azione che resta applicabile sopravviverebbe al filtro
  // conservando un campo che sulla nuova non esiste. Quel difetto non fallisce, valida contro
  // l'entità sbagliata — ed è la ragione per cui si taglia netto invece di filtrare.
  //
  // Si perde anche ciò che sarebbe rimasto valido (un invio mail configurato), ed è una scelta:
  // la stessa che il builder fa da sempre con le condizioni, che azzera senza chiedere. Quando
  // esisterà la UI di configurazione per-azione si potrà fare di meglio — marcare le righe da
  // rivedere invece di buttarle — perché a quel punto ci sarà anche qualcosa da mostrare.
  const handleObjectChange = (cn: string) => {
    setClassName(cn);
    setEventCode('');
    setEventConfig({});
    setRepeatable(false);
    setGroups([{ id: nextGroupIdRef.current++, conditions: [] }]);
    setActions([]);
  };

  // Cambio evento: azzero i parametri (schema di config diverso) e il riscatto, che qualifica
  // l'evento e non l'automazione in astratto — "ripeti" scelto per "nessuna attività da N giorni"
  // non è una scelta fatta per l'evento successivo. Senza, cambiando evento la spunta sparisce dalla
  // vista ma resta accesa nello stato, e riaffiora su un altro evento temporale come se l'utente
  // l'avesse messa lì.
  const handleEventChange = (code: string) => {
    setEventCode(code);
    setEventConfig({});
    setRepeatable(false);
  };

  // ─── Pannello Validazione — l'esito lo dà il BACKEND ─────────────────────────────────────
  // Le regole non sono qui: `validateAutomation` chiama POST /automations/{groupId}/validate, che
  // esegue le stesse validate() della pubblicazione e ne raccoglie le violazioni. Se il pannello è
  // verde, "Attiva automazione" non può fallire — e viceversa non si può attivare col rosso.
  const violations = useAppSelector((s) => s.automations.violations);

  // Senza oggetto ED evento non c'è nulla da validare: il catalogo non saprebbe quali parametri
  // attendersi. Finché mancano, il pannello lo dice e non si chiama il server. La validazione gira
  // ANCHE in read-only: su un record incompleto (es. un template abbozzato) il pannello mostra cosa
  // manca — utile in consultazione, anche se da qui non si corregge.
  const isValidatable = Boolean(className && eventCode);

  // Payload della bozza: lo STESSO della create, di cui eredita i campi required — da cui il
  // segnaposto sul `name`, che questo endpoint non guarda. `eventConfig` viene serializzato qui nel
  // formato del catalogo (mappa nomeParametro → ParamValue tipizzato): è lo stesso pezzo che servirà
  // al submit, quindi nasce già esercitato a ogni pausa di digitazione.
  // La categoria non entra nel giudizio: la testata è validata dal backend al salvataggio.
  const validatePayload = useMemo<AutomationCreatePayload>(
    () => ({
      // Trimmato come lo trimma il salvataggio: è lo stesso payload che serve al confronto con lo
      // snapshot, e uno spazio in coda lo farebbe risultare disallineato da ciò che è finito in DB.
      name: name.trim() || VALIDATION_PLACEHOLDER_NAME,
      description,
      className,
      eventType: eventCode,
      automationCategoryId,
      template: isTemplateRoute,
      eventConfig: eventConfigToDto(selectedEvent?.params ?? [], eventConfig),
      // Su un evento non schedulato il flag non viene mandato affatto, invece di essere mandato a
      // false: la spunta è nascosta, quindi il valore che l'utente vede è "assente", non "spento".
      // Mandarlo comunque farebbe fallire la validazione su un'automazione che a video non ha
      // nessuna casella spuntata.
      repeatable: selectedEvent?.scheduled ? repeatable : false,
      // Stessa regola del riscatto qui sopra, con un'altra condizione: sui TEMPLATE la spunta è
      // nascosta perché un template non gira e non produce storico, quindi il valore che l'utente
      // vede è "assente" e non "spento". Mandarne uno inventato scriverebbe sul record una scelta
      // che nessuno ha fatto.
      logEveryRecord: isTemplateRoute ? false : logEveryRecord,
      conditionGroups: conditionGroupsToDto(groups, conditionFields),
      // In edit `actions` è idratato dal record (fase 1), quindi serializzarlo è sicuro: il backend
      // allinea per-diff sull'automationActionId e non orfanizza le esecuzioni. La vecchia guardia
      // `isEdit ? undefined` non serve più.
      actions: actionsToDto(actions),
    }),
    [
      name,
      description,
      className,
      eventCode,
      automationCategoryId,
      isTemplateRoute,
      selectedEvent,
      eventConfig,
      repeatable,
      logEveryRecord,
      groups,
      conditionFields,
      actions,
    ],
  );

  // ─── Allineamento col DB ──────────────────────────────────────────────────────────────────────
  // Serializzazione del payload corrente. Stessa fonte della validazione, quindi due domande diverse
  // si rispondono sullo stesso oggetto: "questa configurazione sta in piedi?" (il pannello) e
  // "questa configurazione è quella salvata?" (qui).
  const currentSnapshot = useMemo(() => JSON.stringify(validatePayload), [validatePayload]);
  // Modifiche pendenti, cioè non ancora in DB. Simula e Attiva lavorano entrambe sul RECORD e non su
  // ciò che è a video: la prima simula quello che il server ha, la seconda pubblica quello che il
  // server ha. Con modifiche pendenti risponderebbero su una configurazione diversa da quella
  // guardata — per questo restano spente finché non si salva.
  const isDirty = savedSnapshot === null || currentSnapshot !== savedSnapshot;

  // Idratazione in corso: record atteso o arrivato ma non ancora riversato per intero nel form (la
  // fase 2 attende il catalogo condizioni, quindi c'è una round trip fra le due). In questa finestra
  // il corpo va tenuto inerte: quello che si scrive verrebbe sovrascritto dalla fase 1, e — peggio —
  // finirebbe dentro lo snapshot che l'effetto qui sotto posa a idratazione conclusa, facendo passare
  // per "salvato" ciò che non lo è. È la stessa ragione per cui `hydratedRecordId` è uno stato.
  const isHydrating = Boolean(id) && hydratedRecordId !== Number(id);

  // Posa lo snapshot quando il form è allineato al record: a idratazione conclusa (entrambe le fasi)
  // o dopo un salvataggio, che lo azzera per farlo riprendere qui. Gira nel render SUCCESSIVO a
  // quello in cui l'idratazione ha impostato i campi, quindi `currentSnapshot` è già quello buono:
  // `hydratedRecordId` è stato e non ref proprio per garantire quel render.
  useEffect(() => {
    if (!id || hydratedRecordId !== Number(id)) return;
    if (savedSnapshot !== null) return;
    setSavedSnapshot(currentSnapshot);
  }, [id, hydratedRecordId, savedSnapshot, currentSnapshot]);

  // Rivalida a ogni modifica, ma dopo una pausa: una chiamata per pausa di digitazione, non per
  // battuta. Il cleanup annulla il timer precedente, quindi mentre l'utente scrive non parte nulla.
  useEffect(() => {
    // Bozza non ancora validabile: si azzera l'esito precedente, che parlava di un'altra
    // configurazione (es. dopo aver cambiato oggetto, che resetta l'evento).
    if (!isValidatable) {
      dispatch(clearAutomationViolations());
      return;
    }
    const timer = setTimeout(() => {
      void dispatch(validateAutomation(validatePayload));
    }, VALIDATION_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [dispatch, isValidatable, validatePayload]);

  // `null` = non validata (bozza incompleta, validazione in volo, o chiamata fallita): il pannello
  // non si pronuncia. Diverso da `[]`, che è il verde vero. Parla del PAYLOAD A VIDEO, non del record.
  const isValidated = violations !== null && violations.length === 0;

  // Attiva e Simula pongono la stessa domanda — "il RECORD IN DB è pronto?" — e non due domande
  // diverse: entrambe agiscono sul record, non su ciò che è a video. Serve che il record esista (su
  // /templates/new non c'è), che la configurazione sia validata e che non ci siano modifiche
  // pendenti, perché il verde riguarda il payload locale e solo l'assenza di pendenze lo trasferisce
  // al record. Una condizione sola, due pulsanti: nomi separati direbbero una differenza che non c'è.
  const canActOnRecord = Boolean(id) && isValidated && !isDirty;

  // Chi si può eliminare da questa pagina, secondo il vincolo che applica il server
  // (AutomationDefinitionServiceImpl, ActiveNotDeletable): i TEMPLATE sempre — un template attivo è
  // un blueprint in gallery, non sta girando, e non c'è nulla da fermare prima — le AUTOMAZIONI in
  // ogni stato tranne ATTIVO.
  // `currentItem !== null` esplicito, e non il solo `?.status !== 'active'`: una disuguaglianza è
  // VERA anche a record non ancora caricato, e su un'automazione attiva il pulsante rosso
  // comparirebbe per un istante per poi sparire. È il rovescio del problema che la barra risolve
  // leggendo `currentItem?.status` invece di `displayStatus`, e un pulsante distruttivo è il posto
  // peggiore in cui mostrare uno stato che si sta ancora caricando.
  const canDeleteRecord =
    isTemplateRoute || (currentItem !== null && currentItem.status !== 'active');

  // ─── Salvataggio della bozza (submit) — CREATE su /templates/new, UPDATE su /:id/edit ────────
  // La guardia del salvataggio NON è quella catalog-driven del pannello /validate: è più leggera e
  // pretende solo la testata minima (nome, oggetto, evento, categoria), cfr. service.validate. Quindi
  // "Salva bozza" si abilita su questi 4 campi — indipendentemente dal verde della validazione — e una
  // bozza a metà (config incompleta) è salvabile.
  const [saving, setSaving] = useState(false);
  // Solo le DRAFT sono modificabili: in creazione `status` è 'draft'; in edit lo idrata il record. Un
  // record non-DRAFT aperto in edit resta a video ma non salvabile (`status !== 'draft'`). La rotta
  // da-template (/templates/:id: id presente, non edit) è deferita, quindi qui non abilita il salvataggio.
  const canSaveDraft =
    !saving &&
    (isNewTemplate || (isEdit && status === 'draft')) &&
    Boolean(name.trim()) &&
    Boolean(className) &&
    Boolean(eventCode) &&
    automationCategoryId > 0;

  // Cosa manca per SALVARE, in parole. Sono i due requisiti di `canSaveDraft` che nessuno racconta
  // a video: oggetto ed evento li dice già il pannello (`valNotReady` prima, il blocco QUANDO poi),
  // nome e categoria no — e finora "Salva bozza" restava spento senza dire quale dei due mancasse.
  // La CATEGORIA è il caso che inganna davvero: sta fra le pastiglie di stato, quindi si legge come
  // un'etichetta invece che come un campo, e /validate non la guarda — l'utente vede il pannello
  // verde e il pulsante spento, che è la combinazione peggiore.
  // La chiave è il CAMPO e non il messaggio: quest'ultimo è già localizzato, quindi cambierebbe
  // identità al cambio di lingua — un remount per nulla — e due testi che un giorno coincidessero
  // darebbero chiavi duplicate. Costruito con push invece che filtrando: la lista nasce qui, e così
  // resta tipizzata senza cast né type predicate.
  const draftRequirements: { key: string; message: string }[] = [];

  if (!name.trim())
    draftRequirements.push({ key: 'name', message: t('automation.form.builder.valDraftName') });

  if (automationCategoryId <= 0)
    draftRequirements.push({
      key: 'category',
      message: t('automation.form.builder.valDraftCategory'),
    });

  // Salva: in edit aggiorna il record (PATCH) e CHIUDE l'editor, tornando alla view read-only del
  // record; in creazione crea la bozza (nasce DRAFT lato server) e apre l'editor del nuovo record.
  // Esito via toast (come il resto dell'app): success su entrambe, error che mostra il messaggio della
  // guardia del service.
  // Il ritorno alla view chiude il giro con "Attiva", che vive lì: salvare è l'ultimo atto della
  // modifica, e restare nell'editor obbligherebbe a uscirne da sé per pubblicare.
  // La ri-sincronizzazione degli id e l'azzeramento dello snapshot qui sotto servono ANCHE navigando
  // via: view ed editor sono lo stesso componente, quindi lo stato può sopravvivere al cambio di path
  // invece di essere buttato con uno smontaggio.
  const handleSaveDraft = async () => {
    if (!canSaveDraft) return;
    setSaving(true);
    try {
      // Nome normalizzato: canSaveDraft garantisce name.trim() non vuoto, ma il payload spedirebbe
      // il grezzo (spazi in coda compresi) — qui si manda la forma pulita.
      const payload = { ...validatePayload, name: name.trim() };
      if (isEdit) {
        const updated = await dispatch(
          updateAutomation({ ...payload, automationDefinitionId: Number(id) }),
        ).unwrap();
        // Ri-sincronizza gli automationActionId: le azioni appena aggiunte in edit sono state create
        // lato server con un id reale, ma lo stato locale le ha ancora a 0 (l'idratazione non
        // ri-scatta, hydratedIdRef). Senza questo, un SECONDO save consecutivo le rimanderebbe a 0 e
        // il backend le ricreerebbe (delete+recreate, proprio ciò che il per-diff evita). Match per
        // POSIZIONE: `actions` è stato inviato in ordine di priority (index+1) e il finder le ritorna
        // ordinate per priority, quindi `prev[i]` e `updated.actions[i]` sono la stessa azione.
        setActions((prev) =>
          prev.map((row, i) => ({
            ...row,
            automationActionId: updated.actions[i]?.automationActionId ?? row.automationActionId,
          })));
        // Snapshot da riprendere: il payload appena salvato NON è quello di un istante fa — la
        // ri-sincronizzazione qui sopra porta gli `automationActionId` da 0 al valore reale, e
        // `actionsToDto` li serializza. Fotografare adesso lascerebbe il form "sporco" appena salvato.
        // Azzerando, l'effetto di allineamento lo riprende al prossimo render, a id già sincronizzati.
        setSavedSnapshot(null);
        // Chiude l'editor: salvare è l'ultimo atto della modifica, e l'azione che segue — pubblicare
        // — si trova sulla view.
        navigate(recordPath);
      } else {
        const created = await dispatch(createAutomation(payload)).unwrap();
        // Editor del record APPENA CREATO, nel namespace della rotta di creazione: un template creato
        // da /templates/new si riapre come template. Su /automations/:id/edit si presenterebbe come
        // un'automazione — etichette sbagliate, nessun "Usa template", nessun Elimina.
        navigate(`${pathForRecord(created.automationDefinitionId)}/edit`);
      }
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: t('common.error', { message: String(err) }) });
    } finally {
      setSaving(false);
    }
  };

  // "Usa template" (dalla view read-only di un template): crea un'AUTOMAZIONE copiando la config del
  // template (createPayloadFromTemplate → template=false) e va sul suo editor. Stesso meccanismo della
  // card in gallery; qui la sorgente è `currentItem` (il template caricato).
  //
  // L'operazione va resa VISIBILE, e da sola non lo è: la copia ha per definizione la stessa
  // configurazione dell'originale, quindi dopo la navigazione cambia l'URL e nient'altro. Due segnali,
  // uno momentaneo e uno permanente — il toast dice che è successo, il nome dice su cosa ci si trova
  // adesso, e resta a dirlo anche nell'elenco, dove altrimenti comparirebbero due righe gemelle.
  const handleUseTemplate = async () => {
    if (!currentItem || saving) return;
    setSaving(true);
    try {
      const created = await dispatch(
        createAutomation(
          createPayloadFromTemplate(
            currentItem,
            t('automation.form.copyOfName', { name: currentItem.name }),
          ),
        ),
      ).unwrap();
      toast({ severity: 'success', message: t('automation.form.createdFromTemplate') });
      navigate(`/automations/${created.automationDefinitionId}/edit`);
    } catch (err) {
      toast({ severity: 'error', message: t('common.error', { message: String(err) }) });
    } finally {
      // `finally` e non il solo ramo d'errore: si naviga sì, ma verso una rotta servita dallo
      // STESSO componente, che React non rimonta — `saving` sopravvive al cambio di path e
      // resterebbe acceso per sempre, tenendo spento "Salva bozza" (`canSaveDraft` parte da
      // `!saving`) nell'editor appena aperto. Diverso da `handleDelete`, che va su un componente
      // altro e quindi si smonta.
      setSaving(false);
    }
  };

  // "Attiva" (pubblica): dalla view SOLA LETTURA, porta il record DRAFT ad ACTIVE. Gate su
  // `canActOnRecord`, che pretende validazione verde E nessuna modifica pendente: pubblica il record
  // IN DB, quindi con modifiche non salvate pubblicherebbe una configurazione diversa da quella a
  // video — e per giunta mai validata, perché /validate giudica il payload locale. Da qui la seconda
  // condizione è sempre soddisfatta (il corpo è inerte), ma resta scritta: il gate è uno solo e non
  // deve dipendere da quale schermata lo interroga.
  // Il backend RIVALIDA il record dentro la transazione della transizione
  // (AutomationDefinitionLocalServiceImpl.validatePublishable), quindi questo gate non è l'ultima
  // difesa ma la prima: evita un clic destinato a fallire. Il caso in cui il 400 arriva comunque è
  // la corsa — una PATCH concorrente cambia il record fra la validazione e il clic — e allora il
  // messaggio d'errore porta l'elenco delle violazioni, che il thunk estrae dal `detail`.
  // Nessuna navigazione a publish riuscito: la pagina è già quella di destinazione. La barra si
  // riallinea da sé perché publishAutomation.fulfilled riscrive `currentItem` nello slice (come
  // sospendi/riattiva) e le azioni sono lette dal record. `saving` va quindi rilasciato: tenerlo a
  // true — come fanno le azioni che smontano la pagina — lascerebbe spenta l'intera barra ACTIVE
  // appena comparsa.
  const handlePublish = async () => {
    if (!id || !canActOnRecord || saving) return;
    setSaving(true);
    try {
      await dispatch(publishAutomation(Number(id))).unwrap();
      toast({ severity: 'success', message: t('automationsInsights.list.toast.published') });
    } catch (err) {
      toast({ severity: 'error', message: t('common.error', { message: String(err) }) });
    } finally {
      setSaving(false);
    }
  };

  // "Crea nuova versione" (da un record ACTIVE): il backend copia lo stack in una nuova versione DRAFT
  // (la ACTIVE resta finché non si pubblica la nuova); si atterra sull'edit della nuova versione.
  const handleCreateVersion = async () => {
    if (!id || saving) return;
    setSaving(true);
    try {
      const created = await dispatch(createAutomationVersion(Number(id))).unwrap();
      toast({ severity: 'success', message: t('automationsInsights.list.toast.versionCreated') });
      navigate(`${pathForRecord(created.automationDefinitionId)}/edit`);
    } catch (err) {
      toast({ severity: 'error', message: t('common.error', { message: String(err) }) });
    } finally {
      // Vedi `handleUseTemplate`: la nuova versione si apre nell'editor, che è lo stesso
      // componente della view, quindi `saving` va spento a mano anche quando è andato tutto bene.
      setSaving(false);
    }
  };

  // Sospendi/Riattiva (ACTIVE↔SUSPENDED): non si naviga — la transizione aggiorna `currentItem` nello
  // slice, quindi la view read-only si ri-renderizza e i pulsanti cambiano da soli (nessun reload).
  const handleSuspend = async () => {
    if (!id || saving) return;
    setSaving(true);
    try {
      await dispatch(suspendAutomation(Number(id))).unwrap();
      toast({ severity: 'success', message: t('automationsInsights.list.toast.suspended') });
    } catch (err) {
      toast({ severity: 'error', message: t('common.error', { message: String(err) }) });
    } finally {
      setSaving(false);
    }
  };
  const handleResume = async () => {
    if (!id || saving) return;
    setSaving(true);
    try {
      await dispatch(resumeAutomation(Number(id))).unwrap();
      toast({ severity: 'success', message: t('automationsInsights.list.toast.resumed') });
    } catch (err) {
      toast({ severity: 'error', message: t('common.error', { message: String(err) }) });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Spegne o riaccende un'azione dalla view in SOLA LETTURA, dove l'editor non si apre: scrive
   * subito sul record, senza passare dal salvataggio dell'automazione. È la leva per fermare in
   * fretta un'azione che sta agendo male su un'automazione già attiva.
   * <p>Lo stato locale si allinea DOPO la risposta e non prima: un aggiornamento ottimistico qui
   * mostrerebbe l'azione spenta anche quando il server ha rifiutato — e su un comando che serve a
   * fermare qualcosa, credere di averlo fermato è il modo peggiore di sbagliare.
   * <p>Solo su azioni GIÀ PERSISTITE: in sola lettura lo sono tutte, ma la guardia lo dichiara
   * invece di lasciarlo dedurre dal contesto.
   */
  const handleToggleActionEnabled = async (row: ActionRow) => {
    if (saving || row.automationActionId <= 0) return;
    setSaving(true);
    try {
      await dispatch(setAutomationActionEnabled({
        automationActionId: row.automationActionId,
        enabled: !row.enabled,
      })).unwrap();
      toggleAction(row.id);
      // Snapshot da riprendere, come dopo un salvataggio: il flag è ora scritto sul record, quindi
      // il payload appena cambiato NON è una modifica pendente ma il nuovo allineamento. Senza
      // questo, `isDirty` resterebbe vero e la sidebar direbbe di salvare la bozza — su una view in
      // sola lettura, dove non si salva — e la stessa condizione spegnerebbe Simula e Attiva.
      setSavedSnapshot(null);
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: t('common.error', { message: String(err) }) });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Accende o spegne "registra ogni record". Due strade, e la differenza non è il flag ma il
   * RECORD: su un'automazione già salvata si scrive subito con l'endpoint dedicato, che vale in
   * qualunque stato; su una in creazione non c'è ancora niente da aggiornare, quindi si tiene il
   * solo stato locale e sarà il payload di creazione a portarlo.
   * <p>Lo stato locale si aggiorna comunque DOPO la risposta, mai prima: uno switch che si muove e
   * poi torna indietro perché il server ha rifiutato è peggio di uno switch che si muove in
   * ritardo. Stessa scelta di `handleToggleActionEnabled`.
   * <p>`setSavedSnapshot(null)` per la stessa ragione dell'altro handler: il valore è ora sul
   * record, quindi non è una modifica pendente — senza, `isDirty` resterebbe vero e la sidebar
   * chiederebbe di salvare una bozza su una view in sola lettura.
   */
  const handleLogEveryRecordChange = async (next: boolean) => {
    if (saving) return;

    if (!id) {
      setLogEveryRecord(next);

      return;
    }

    setSaving(true);

    try {
      await dispatch(
        setAutomationLogEveryRecord({
          automationDefinitionId: Number(id),
          logEveryRecord: next,
        }),
      ).unwrap();
      setLogEveryRecord(next);
      setSavedSnapshot(null);
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: t('common.error', { message: String(err) }) });
    } finally {
      setSaving(false);
    }
  };

  // "Elimina" (template in qualunque stato, automazioni tranne che attive): unica azione della
  // barra che NON resta in pagina — cancellato il record, la view read-only non ha più nulla da
  // mostrare, quindi si torna all'elenco del ramo da cui si è arrivati (`listPath`). La conferma la
  // chiede il chiamante (il pulsante), qui si esegue e basta: `saving` resta true sul percorso
  // felice perché la pagina sta per essere smontata, e riabilitarlo farebbe lampeggiare i pulsanti
  // — come in handlePublish/handleUseTemplate.
  // Nessuna guardia sullo stato NEMMENO QUI, benché il pulsante ora ne abbia una: chi decide
  // davvero è il servizio remoto (AutomationDefinitionServiceImpl: ActiveNotDeletable), e l'errore
  // arriva tradotto nel catch. La condizione sul pulsante evita un clic destinato a fallire, non
  // sostituisce il vincolo — un record può diventare attivo fra il render e il clic.
  // Il messaggio di esito distingue i due rami: le chiavi esistono già entrambe, quella
  // dell'automazione è la stessa che usa il menu di riga dell'elenco.
  const handleDelete = async () => {
    if (!id || saving) return;
    setSaving(true);
    try {
      await dispatch(deleteAutomation(Number(id))).unwrap();
      toast({
        severity: 'success',
        message: t(
          isTemplateRoute
            ? 'automation.form.templateDeleted'
            : 'automationsInsights.list.toast.deleted',
        ),
      });
      navigate(listPath);
    } catch (err) {
      toast({ severity: 'error', message: t('common.error', { message: String(err) }) });
      setSaving(false);
    }
  };

  // Stato/versione a video: quelli del RECORD (`currentItem`), che si aggiornano anche dopo le
  // transizioni (sospendi/riattiva/pubblica), così i pulsanti read-only reagiscono senza reload. Su
  // /templates/new (nessun record) valgono i default locali (draft/v1).
  const displayStatus = currentItem?.status ?? status;
  const displayVersion = currentItem?.version ?? version;

  // Etichette QUANDO/SE/ALLORA (uppercase via CSS) per il riepilogo.
  const kwWhen = t('automation.form.activation');
  // Il `.replace(':', '')` che stava qui non serve piu': la chiave i18n non
  // porta piu' i due punti (li aggiungeva per un formato in linea che non
  // esiste piu', e la galleria template la rende come etichetta a se').
  const kwIf = t('common.ifLabel');
  const kwThen = t('automation.form.actions');

  // Riepilogo: legge il PAYLOAD (non lo stato dell'editor) e il catalogo, che porta le etichette già
  // localizzate dal converter. La stessa funzione servirà la card della gallery, che riceve
  // un'`Automation` — stessa forma, nessun adattatore.
  const summary = useMemo(
    () =>
      summarizeAutomation(
        validatePayload,
        { objectTypes, conditionFieldsByClassName: conditionTypesByClassName },
        t,
      ),
    [validatePayload, objectTypes, conditionTypesByClassName, t],
  );

  // Blocchi del pannello Validazione, nell'ordine in cui si compongono, con la frase che ciascuno
  // mostra quando è a posto. L'etichetta serve SOLO al verde: un problema si spiega da sé, e
  // premettergli il nome del blocco aggiungerebbe rumore a ciò che l'utente deve leggere.
  // I conteggi si leggono dal PAYLOAD, non dallo stato dell'editor: il numero a video è quello delle
  // cose che il backend ha davvero giudicato, non di quelle che l'utente ha appena iniziato a scrivere.
  const validationBlocks = useMemo<{ block: AutomationBlock; okLabel: string }[]>(
    () => [
      { block: 'WHEN', okLabel: t('automation.form.builder.valOk') },
      {
        // Zero condizioni non è un conteggio a vuoto ma un'informazione ("Nessuna condizione":
        // l'automazione scatterà su OGNI evento dell'entità), e la dice la chiave `_zero`: i18next
        // cerca il suffisso zero quando count è 0, a prescindere dalle regole di plurale della lingua.
        block: 'IF',
        okLabel: t('automation.form.builder.valOkIf', {
          count: (validatePayload.conditionGroups ?? []).reduce(
            (total, group) => total + group.conditions.length,
            0,
          ),
        }),
      },
      {
        block: 'THEN',
        okLabel: t('automation.form.builder.valOkThen', {
          count: (validatePayload.actions ?? []).length,
        }),
      },
    ],
    [t, validatePayload],
  );

  return (
    // Il link "torna" sta FUORI dalla griglia: dentro la colonna sinistra
    // spingeva giu' solo quella, e i box di destra partivano piu' in alto dei
    // pulsanti di modalita'. Fuori, le due colonne cominciano insieme.
    <>
      <Link to={listPath} className="tpl-back builder-back">
        &larr; {t(isTemplateRoute ? 'automation.backToTemplates' : 'automation.backToList')}
      </Link>
      <div className="builder-layout">
      {/* LEFT — colonna scrollabile: titolo + box regola */}
      <div className="builder-main">
        {/* In SOLA LETTURA l'intero corpo è un <fieldset disabled>: input testata, chip (ChipSelect/
            ParamField) e pulsanti di rimozione diventano inerti in un colpo solo. Gli "add" del SE/ALLORA
            sono <a> (non disabilitabili dal fieldset) e vengono nascosti a parte più sotto.
            Inerte anche durante l'IDRATAZIONE, per la ragione spiegata su `isHydrating`: scrivere in
            quella finestra sporcherebbe lo snapshot dell'allineamento. */}
        <fieldset disabled={isReadOnly || isHydrating} className="builder-fieldset">
        {/* Barra titolo (resta sulla fascia sinistra) */}
        <div className="builder-topbar">
          <div className="builder-topbar__title">
            <div className="builder-titlefields">
              {/* Ordine del concept: prima la riga delle pastiglie (stato,
                  versione, categoria), POI il titolo, poi la descrizione. Prima
                  stavano tutte sulla stessa riga e il nome finiva schiacciato
                  fra i chip. */}
              <div className="builder-metarow">
                {/* Badge = stato corrente + versione (in edit dal record). Colore dalla stessa mappa
                    dei chip di elenco/gallery (STATUS_CHIP_COLOR): active→verde, suspended→giallo,
                    draft/archived→grigio (default = nessuna variante). */}
                <span
                  className={`builder-badge${
                    STATUS_CHIP_COLOR[displayStatus] !== 'default'
                      ? ` builder-badge--${STATUS_CHIP_COLOR[displayStatus]}`
                      : ''
                  }`}
                >
                  {t(`automationsInsights.list.statuses.${displayStatus}`)} · v{displayVersion}
                </span>
                {/* Categoria come chip: riusa ChipSelect (Menu MUI, dropdown coerente col resto del
                    builder) con i colori della categoria scelta (backColor/fontColor) sul chip; neutro
                    come placeholder. Obbligatoria al salvataggio (categoryId > 0), ignorata da /validate. */}
                <ChipSelect
                  className="builder-catchip"
                  value={automationCategoryId ? String(automationCategoryId) : ''}
                  options={categories.map((c) => ({
                    value: String(c.automationCategoryId),
                    label: c.name,
                  }))}
                  placeholder={t('automation.form.builder.categoryPlaceholder')}
                  onChange={(v) => setAutomationCategoryId(Number(v))}
                  style={
                    selectedCategory
                      ? { backgroundColor: selectedCategory.backColor, color: selectedCategory.fontColor }
                      : undefined
                  }
                />
              </div>
              <input
                className="builder-titleinput"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('automation.form.builder.namePlaceholder')}
                aria-label={t('automation.form.builder.namePlaceholder')}
              />
              {/* Descrizione multiriga: textarea che cresce col contenuto (altezza gestita dall'effetto
                  di auto-grow su `description`, che copre digitazione e idratazione). rows=1 di base. */}
              <textarea
                ref={descRef}
                className="builder-descinput"
                value={description}
                rows={1}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('automation.form.builder.descriptionPlaceholder')}
                aria-label={t('automation.form.builder.descriptionPlaceholder')}
              />
            </div>
          </div>
        </div>

        {/* OPZIONI DELL'AUTOMAZIONE — fuori dai tre blocchi, e non è una questione di layout: QUANDO,
            SE e ALLORA sono la CONFIGURAZIONE, cioè ciò che l'automazione fa, e si modificano solo
            sulle bozze perché cambiarli su un'automazione attiva significa cambiarne il
            comportamento. Quanto storico tenere non appartiene a quel gruppo: l'automazione resta
            scritta e si comporta identica, cambia solo quanto se ne legge nel monitoraggio.
            Per questo la riga vive fuori e resta VIVA anche in sola lettura, quando tutto il resto
            della pagina è congelato: chi si accorge che il log si sta gonfiando lo scopre mentre
            l'automazione gira, e obbligarlo a una nuova versione vorrebbe dire ripubblicare
            un'automazione che funziona per una scelta che non riguarda cosa fa.
            Assente sui TEMPLATE: non girano, quindi non producono storico. */}
        {!isTemplateRoute && (
          <div className="builder-optionsrow">
            <Tooltip title={t('automation.form.builder.logEveryRecordHint')} placement="top" arrow>
              {/* `div role="button"` e NON uno <Switch> di MUI, per la stessa ragione per cui
                  l'interruttore delle azioni non è un <button>: il corpo del builder è un
                  `<fieldset disabled>` in sola lettura, e un fieldset disabilitato spegne ogni
                  controllo di form al suo interno — qualunque cosa dica il suo `disabled`. Uno
                  Switch, che è un input, resterebbe morto proprio nel caso per cui questa riga
                  esiste: cambiare il flag su un'automazione ATTIVA senza crearne una versione
                  nuova. Un elemento non-form sfugge al fieldset.
                  Il prezzo è la semantica da ricostruire a mano — `role`, `tabIndex`, la tastiera
                  e `aria-pressed` — ed è lo stesso prezzo che il file paga già due volte più
                  sopra. */}
              <span
                role="button"
                tabIndex={0}
                className="builder-fieldnum builder-optionsrow__toggle"
                aria-pressed={logEveryRecord}
                aria-disabled={saving}
                onClick={() => handleLogEveryRecordChange(!logEveryRecord)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleLogEveryRecordChange(!logEveryRecord);
                  }
                }}
              >
                {t('automation.form.builder.logEveryRecord')}
                <span aria-hidden="true">{logEveryRecord ? '◉' : '○'}</span>
              </span>
            </Tooltip>
          </div>
        )}

        {/* QUANDO */}
        <section className="builder-card">
          <div className="builder-card__head">
            <span className="builder-kw builder-kw--when">{t('automation.form.activation')}</span>
            <span className="builder-card__hint">{t('automation.form.builder.whenHint')}</span>
          </div>
          <div className="builder-card__body">
            <ChipSelect
              value={className}
              options={objectOptions}
              placeholder={t('automation.form.builder.selectObject')}
              onChange={handleObjectChange}
            />
            <span className="builder-arrow">▸</span>
            <ChipSelect
              value={eventCode}
              options={eventOptions}
              placeholder={
                className
                  ? t('automation.form.builder.selectEvent')
                  : t('automation.form.builder.noObjectFirst')
              }
              onChange={handleEventChange}
              disabled={!className}
            />
            {/*
              PRIMA dei parametri, e non in coda: il riscatto c'è sempre su un evento schedulato,
              mentre i parametri sono opzionali e cambiano da evento a evento. In coda finirebbe in
              una posizione che si sposta a ogni evento diverso, e su un evento senza parametri
              sarebbe l'unico elemento — quindi "in fondo" e "in testa" sarebbero lo stesso posto.
              Davanti sta fermo.

              Solo sugli eventi SCHEDULATI: su un evento real-time non c'è niente da ripetere — un
              salvataggio avviene una volta — e offrire la scelta significherebbe accettare una
              configurazione che il validatore poi rifiuta. La condizione è dichiarata dal catalogo
              (`scheduled`), non dedotta dal codice dell'evento: un evento temporale nuovo eredita
              la spunta senza toccare questo file.
            */}
            {selectedEvent?.scheduled && (
              // Tooltip di MUI e non l'attributo `title`: il tooltip nativo del browser non si
              // stila, compare in ritardo e su un testo lungo diventa un blocco illeggibile. È già
              // il modo dell'app — AutomationList, Sidebar, ActivityTimeline — quindi qui non si
              // introduce niente di nuovo.
              <Tooltip title={t('automation.form.builder.repeatableHint')} placement="top" arrow>
                <span className="builder-fieldnum">
                  {t('automation.form.builder.repeatable')}
                  <Switch
                    size="small"
                    checked={repeatable}
                    onChange={(e) => setRepeatable(e.target.checked)}
                  />
                </span>
              </Tooltip>
            )}
            {params.map((p) => (
              <ParamField
                key={p.name}
                param={p}
                value={eventConfig[p.name]}
                onChange={(v) => setEventConfig((prev) => ({ ...prev, [p.name]: v }))}
              />
            ))}
          </div>
        </section>

        {/* SE
            GEMELLO: l'editor del filtro della board (`KanbanFilterEditor.tsx`, TS-102) e'
            fatto per essere identico a vista a questo blocco, ma e' codice indipendente: la
            parita' la tengono solo le classi `builder-*` condivise. Chi tocca la resa di qua
            guardi anche di la'. */}
        <section className="builder-card">
          <div className="builder-card__head">
            <span className="builder-kw builder-kw--if">{t('automation.form.conditions')}</span>
            <span className="builder-card__hint">{t('automation.form.builder.ifHint')}</span>
          </div>
          <div className="builder-card__body">
            {!className ? (
              // Le condizioni dipendono dall'oggetto: prima va scelto nel QUANDO.
              <span className="builder-card__hint">{t('automation.form.builder.noObjectFirst')}</span>
            ) : (
              <>
                {groups.map((g, gi) => (
                  // width:100% (builder-cgroup-wrap): il body della card è flex-wrap, il wrapper
                  // deve prendere l'intera riga così i gruppi si impilano invece di affiancarsi.
                  <div key={g.id} className="builder-cgroup-wrap">
                    {/* Separatore OR dal secondo gruppo (i gruppi sono in OR tra loro). */}
                    {gi > 0 && (
                      <div className="builder-or">{t('automation.form.builder.logicOr')}</div>
                    )}
                    <div className="builder-cgroup">
                      <div className="builder-glabel">
                        <span>{t('automation.form.builder.allTrue')}</span>
                        {groups.length > 1 && (
                          <button
                            type="button"
                            className="builder-crow__remove"
                            aria-label={t('automation.form.builder.removeGroup')}
                            onClick={() => removeGroup(g.id)}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      {g.conditions.map((c, ci) => (
                        <ConditionRowEditor
                          key={c.id}
                          fields={conditionFields}
                          condition={c}
                          showLogic={ci > 0}
                          onChange={(next) => updateCondition(g.id, c.id, next)}
                          onRemove={() => removeCondition(g.id, c.id)}
                        />
                      ))}
                      {!isReadOnly && (
                        <a className="builder-add" onClick={() => addCondition(g.id)}>
                          ＋ {t('automation.form.addCondition')}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
                {!isReadOnly && (
                  <a className="builder-add builder-add--group" onClick={addGroup}>
                    ＋ {t('automation.form.builder.addGroup')}
                  </a>
                )}
              </>
            )}
          </div>
        </section>

        {/* ALLORA */}
        <section className="builder-card">
          <div className="builder-card__head">
            <span className="builder-kw builder-kw--then">{t('automation.form.actions')}</span>
            <span className="builder-card__hint">{t('automation.form.builder.thenHint')}</span>
          </div>
          <div className="builder-card__body">
            {/* Le azioni dipendono dall'oggetto quanto le condizioni: il catalogo si chiede per
                className e ogni azione si autofiltra con isApplicable, quindi senza oggetto non
                c'è nulla da offrire. Senza questo avviso il pulsante restava attivo e la modale si
                apriva su "Nessun risultato", che dice "azioni non ne esistono" invece di "manca un
                passo prima" — e chi legge non ha modo di distinguere le due cose.
                Stessa risposta del blocco SE e stessa chiave, che infatti non nomina le condizioni.
                Non serve un ternario intorno all'elenco: senza oggetto non ci sono azioni — una
                bozza nuova non ne ha, e cambiare oggetto le azzera (handleObjectChange) — quindi il
                map qui sotto non rende comunque nulla. */}
            {!className && (
              <span className="builder-card__hint">{t('automation.form.builder.noObjectFirst')}</span>
            )}
            {actions.map((a, i) => {
              // Il tipo si risolve UNA volta per riga: icona e descrizione vengono dalla stessa
              // voce di catalogo, e cercarla due volte direbbe che sono due cose scollegate.
              // Assente finché il catalogo non è arrivato, o se il codice non è più registrato:
              // l'icona ricade sulla generica, la descrizione semplicemente non compare.
              const actionType = actionTypes.find((at) => at.code === a.code);

              return (
                <ActionRowView
                  key={a.id}
                  index={i}
                  total={actions.length}
                  title={a.title}
                  description={actionType?.description}
                  icon={actionType?.icon ?? DEFAULT_ACTION_ICON}
                  onError={a.onError}
                  enabled={a.enabled}
                  busy={isReadOnly && saving}
                  readOnly={isReadOnly}
                  onEdit={() => setEditing(a)}
                  onMove={(delta) => moveAction(i, delta)}
                  onRemove={() => removeAction(a.id)}
                  // In modifica il flag viaggia col payload al salvataggio, in sola lettura si
                  // scrive subito sul record: sono i due soli modi in cui quel valore può cambiare,
                  // e la riga non deve sapere quale dei due la sta servendo.
                  onToggle={() =>
                    (isReadOnly ? handleToggleActionEnabled(a) : toggleAction(a.id))}
                />
              );
            })}
            {/* `undefined` = modale in AGGIUNTA (vedi `editing`). Non è una combo: la scelta apre un
                flusso, che ospiterà anche il form di configurazione dell'azione.
                <button> e non <a> come gli "aggiungi" del SE: un <a> senza href non prende il fuoco
                da tastiera e non si attiva con Invio. `.builder-add` ha già il reset da button. */}
            {!isReadOnly && Boolean(className) && (
              <button type="button" className="builder-add" onClick={() => setEditing(undefined)}>
                ＋ {t('automation.form.addAction')}
              </button>
            )}
          </div>
        </section>
        </fieldset>
      </div>

      {/* RIGHT — fascia destra fissa (come .detail-right) con box separati */}
      <aside className="builder-side">
        <div className="builder-side__box">
          <h3>{t('automation.form.summary')}</h3>
          {/* Il riepilogo racconta il PAYLOAD, non lo stato dell'editor: la stessa forma che legge la
              card della gallery, quindi una resa sola per i due posti. Ciò che non è stato ancora
              scelto compare come segnaposto — il riepilogo è sempre una frase intera, e mostra la
              forma di ciò che manca. Il blocco ALLORA resta segnaposto finché il payload non porta
              le azioni: la serializzazione è la fetta successiva (nella gallery, che legge record
              persistiti, il riepilogo le nomina già). */}
          <div className="builder-nl">
            <span className="builder-nl__when">{kwWhen}</span> {summary.when}
            <br />
            <span className="builder-nl__if">{kwIf}</span>{' '}
            <AutomationSummaryTokens tokens={summary.if} />
            <br />
            <span className="builder-nl__then">{kwThen}</span>{' '}
            {summary.then.length > 0 ? summary.then.join(' · ') : SUMMARY_PLACEHOLDER}
          </div>
        </div>

        {/* Pannello Validazione: visibile sempre, anche in sola lettura — consultando un record
            incompleto (es. un template abbozzato) serve vedere cosa manca. Qui è informativo: da una
            view read-only non si corregge, ma si sa se l'automazione "sta in piedi". */}
        <div className="builder-side__box">
          <h3>{t('automation.form.builder.validation')}</h3>
          {!isValidatable ? (
            // Oggetto o evento non ancora scelti: non si chiama il server (non saprebbe quali
            // parametri attendersi) e il pannello dice cosa manca per poter giudicare.
            <div className="builder-val text-muted">{t('automation.form.builder.valNotReady')}</div>
          ) : violations === null ? (
            // Validazione in volo (o fallita): il pannello tace. Mostrare il verde qui sarebbe una
            // bugia — non sappiamo se l'automazione sta in piedi.
            <div className="builder-val text-muted">{t('automation.form.builder.valPending')}</div>
          ) : (
            // Esito PER BLOCCO. Ogni violazione dichiara il blocco che l'ha prodotta, quindi un blocco
            // che non compare in nessuna è a posto: senza questa lettura, un QUANDO valido sarebbe
            // indistinguibile da un QUANDO non guardato — a video si vedrebbe solo ciò che manca
            // altrove. I blocchi si elencano SEMPRE tutti e tre, nell'ordine in cui si compongono.
            validationBlocks.map(({ block, okLabel }) => {
              const blockViolations = violations.filter((violation) => violation.block === block);
              if (blockViolations.length === 0) {
                return (
                  <div className="builder-val" key={block}>
                    <span className="builder-val__ok"><CheckGlyph /></span> {okLabel}
                  </div>
                );
              }
              // Le violazioni non hanno id: la lista è ricostruita per intero a ogni risposta e non
              // è riordinabile né editabile, quindi l'indice come key è stabile quanto serve.
              return blockViolations.map((violation, index) => (
                <div className="builder-val" key={`${block}-${index}`}>
                  <span className="builder-val__warn"><AlertGlyph /></span> {violation.message}
                </div>
              ));
            })
          )}
          {/* Requisiti del SALVATAGGIO, in coda ai blocchi e separati da una linea. Quelli sopra
              dicono se l'automazione starà in piedi una volta pubblicata e vengono dal server;
              questi dicono perché "Salva bozza" è spento, e li calcola il client. Sono due domande
              diverse, e senza il separatore si leggerebbero come un elenco solo.
              Stanno FUORI dai rami di sopra di proposito: così restano visibili anche quando il
              pannello non è ancora in grado di giudicare — oggetto o evento non scelti — che è
              esattamente lo stato in cui si apre /templates/new. Se vivessero dentro quel ramo
              sarebbero invisibili proprio quando servono.
              Non in sola lettura: da lì non si salva, e il pannello è informativo. */}
          {!isReadOnly && draftRequirements.length > 0 && (
            <div className="builder-valdraft">
              {draftRequirements.map((requirement) => (
                <div className="builder-val" key={requirement.key}>
                  <span className="builder-val__warn"><AlertGlyph /></span> {requirement.message}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="builder-actbar">
          {isReadOnly ? (
            // View SOLA LETTURA: azioni stato-driven, lette dal RECORD così le transizioni le
            // aggiornano senza reload. DRAFT → Attiva, Modifica; ACTIVE → Nuova versione, Sospendi,
            // (template) Usa; SUSPENDED → Riattiva; ARCHIVED → nulla. In coda, fuori dal ternario,
            // Simula — che non dipende dallo stato — ed Elimina, che invece ne dipende ma con una
            // regola sua: i template sempre, le automazioni tranne che attive.
            <>
              {/* `currentItem?.status` e NON `displayStatus`: quest'ultimo ripiega sul default locale
                  'draft' finché il record non è caricato, quindi su un record attivo la barra
                  mostrerebbe per un istante le azioni della bozza. Attiva sarebbe innocua, perché
                  nasce spenta e resta tale finché non c'è un record; Modifica no — è un Link sempre
                  abilitato, e chi lo cliccasse in quella finestra finirebbe su /edit per essere
                  rimbalzato dalla guardia di accesso. Gli altri rami leggono `displayStatus`, che
                  ripiegando sulla bozza non li fa comparire affatto: solo qui il default è anche il
                  valore che si sta cercando. */}
              {currentItem?.status === 'draft' && (
                <>
                  {/* Attiva in testa e primaria: è l'azione terminale della bozza, quella per cui la
                      si riapre una volta finita, mentre Modifica la emenda — stessa lettura del
                      template attivo, dove "Usa" precede le azioni che lo amministrano.
                      Vive nella view e non nell'editor perché pubblica il RECORD IN DB: `canActOnRecord`
                      pretende che non ci siano modifiche pendenti, condizione che da qui è sempre vera
                      (il corpo è inerte) e che nell'editor è falsa proprio mentre lo si sta usando.
                      Resta quindi il solo requisito vero: validazione verde.
                      Su una bozza incompleta è il primo pulsante della barra a presentarsi grigio:
                      accettato, perché il pannello qui sopra dice già cosa manca. Non è l'ultima difesa
                      — il service rivalida il record dentro la transazione — ma evita un clic destinato
                      a fallire. */}
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={!canActOnRecord || saving}
                    onClick={handlePublish}
                  
            startIcon={<CheckGlyph />}>
                    {t(
                      isTemplateRoute
                        ? 'automation.form.activateTemplate'
                        : 'automation.form.activateAutomation',
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    component={Link}
                    to={recordEditPath}
                  
            startIcon={<EditGlyph />}>
                    {t('automationsInsights.list.actions.edit')}
                  </Button>
                </>
              )}
              {displayStatus === 'active' && (
                <>
                  {/* Usa template in testa: su un template attivo è l'azione per cui la pagina viene
                      aperta — le altre due lo amministrano, questa lo mette in uso. Sulle automazioni
                      non esiste, e lì la prima è "Nuova versione". */}
                  {isTemplateRoute && (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={saving}
                      onClick={handleUseTemplate}
                      startIcon={<CheckGlyph />}
                    >
                      {t('automation.useTemplate')}
                    </Button>
                  )}
                  {/* Nuova versione: primaria (contained) sulle automazioni, dove non c'è "Usa
                      template" a fare da azione primaria; sui template resta outlined. */}
                  <Button
                    variant={isTemplateRoute ? 'outlined' : 'contained'}
                    color="primary"
                    fullWidth
                    disabled={saving}
                    onClick={handleCreateVersion}
                  
            startIcon={<LayersGlyph />}>
                    {t('automationsInsights.list.actions.newVersion')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    fullWidth
                    disabled={saving}
                    onClick={handleSuspend}
                  
            startIcon={<PauseGlyph />}>
                    {t('automationsInsights.list.actions.suspend')}
                  </Button>
                </>
              )}
              {displayStatus === 'suspended' && (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={saving}
                  onClick={handleResume}
                
            startIcon={<PlayGlyph />}>
                  {t('automationsInsights.list.actions.resume')}
                </Button>
              )}
            </>
          ) : (
            <Button variant="outlined" fullWidth disabled={!canSaveDraft} onClick={handleSaveDraft}
            startIcon={<CheckGlyph />}>
              {t('automation.form.saveDraft')}
            </Button>
          )}
          {/* Monitora: completa la simmetria con Simula, e le sta PRIMA perché guarda al passato —
              cos'è già successo — mentre Simula guarda a cosa succederebbe. Due condizioni, per
              ragioni diverse. NON sui template, che non eseguono e per i quali una rotta di
              monitoraggio non esiste nemmeno: usare recordPath senza questa guardia produrrebbe un
              URL morto. Non sulle BOZZE, che non hanno mai prodotto un run — stessa condizione del
              menu di riga dell'elenco, così le due strade verso la stessa pagina non dicono cose
              diverse sullo stesso record.
              Nessun disabled: dove non ha senso il pulsante non c'è, perché a differenza di Simula
              — che può tornare disponibile salvando o validando — qui la condizione non dipende da
              cosa fa l'utente su questa pagina. */}
          {!isTemplateRoute && displayStatus !== 'draft' && (
            <Button variant="outlined" fullWidth component={Link} to={`${recordPath}/monitoring`}>
              {t('automationsInsights.list.actions.monitor')}
            </Button>
          )}
          {/* Simula: la simulazione gira sul RECORD, quindi serve che ci sia (niente id su
              /templates/new), che sia validato (una configurazione incoerente non è simulabile) e che
              non ci siano modifiche pendenti — altrimenti si simulerebbe una versione diversa da
              quella a video. Il Link si attacca solo da abilitato: un <a> disabilitato resta
              navigabile da tastiera. Rotta simmetrica al ramo (template vs automazione). */}
          <Button
            variant="outlined"
            fullWidth
            disabled={!canActOnRecord}
            {...(canActOnRecord && { component: Link, to: `${recordPath}/simulation` })}
          
            startIcon={<PlayGlyph />}>
            {t('automation.form.builder.simulate')}
          </Button>
          {/* Elimina: in sola lettura e secondo `canDeleteRecord` (template sempre, automazioni
              tranne che attive).
              Sui template questa pagina resta l'UNICA strada — la gallery non ha azioni di riga, e
              limitarlo alla bozza lascerebbe un template pubblicato senza modo di sparire. Sulle
              automazioni la strada c'è anche dall'elenco, dove però la voce è SEMPRE presente e
              sulle attive fallisce con un toast. Qui invece si nasconde, e le due scelte non si
              contraddicono: nel menu di riga l'errore è l'unico modo di dire "sospendi prima",
              mentre in questa pagina il pulsante Sospendi sta due posizioni più su, e mostrarne uno
              che fallisce accanto a quello che lo renderebbe possibile direbbe la stessa cosa
              peggio.
              ULTIMA della barra, sotto anche a Simula che è condivisa fra i due rami: è l'unica
              distruttiva, e in fondo non si interpone fra le azioni che l'utente cerca davvero. Sta
              quindi FUORI dal ternario read-only/edit, e da lì la condizione read-only va scritta per
              esteso — il prezzo di stare dopo qualcosa che i due rami condividono.
              Distruttiva: contained + error (rosso pieno, testo bianco), che il colore lo dica prima
              che si legga l'etichetta. Passa sempre dalla conferma. */}
          {isReadOnly && canDeleteRecord && (
            <Button
              variant="contained"
              color="error"
              fullWidth
              disabled={saving}
              onClick={() =>
                confirm.requestConfirm(
                  t(
                    isTemplateRoute
                      ? 'automation.form.confirmDeleteTemplate'
                      : 'automationsInsights.list.confirmDelete',
                  ),
                  handleDelete,
                  // Parola da ridigitare: eliminare un'automazione o un template non si disfa, e
                  // da qui si parte con il record già aperto sotto gli occhi — il contesto in cui
                  // è più facile premere per inerzia.
                  { requireTypedConfirmation: true },
                )
              }
            
            startIcon={<TrashGlyph />}>
              {t('automationsInsights.list.actions.delete')}
            </Button>
          )}
          {/* Perché i pulsanti sono spenti. Solo nel caso non deducibile: col pannello VERDE e le
              modifiche pendenti, a video non c'è nulla che spieghi il grigio — il rosso del pannello
              invece parla da sé, e ripeterlo qui sarebbe rumore. Sul record non ancora caricato tace,
              che non è una condizione su cui l'utente possa agire.
              Parla per Simula, l'unica azione della barra soggetta a `canActOnRecord` che l'editor
              ancora ospita: sul record con modifiche pendenti simulerebbe una configurazione diversa
              da quella a video. */}
          {id && !isHydrating && isValidated && isDirty && (
            <p className="text-sm text-muted">{t('automation.form.saveBeforeActing')}</p>
          )}
        </div>
      </aside>

      {/* Fuori dal <fieldset disabled> del builder: MUI la porta comunque in un portale, ma tenerla
          qui dice che non è parte del corpo read-only — si apre solo da azioni che il read-only
          nasconde. `editing !== null` e non `editing &&`: `undefined` è l'aggiunta, non "chiusa". */}
      {editing !== null && (
        <AutomationActionModal
          action={editing}
          actionTypes={actionTypes}
          className={className}
          readOnly={isReadOnly}
          onClose={() => setEditing(null)}
          onConfirm={upsertAction}
        />
      )}

      <ConfirmDialog
        open={confirm.isOpen}
        message={confirm.message}
        {...confirm.options}
        onConfirm={confirm.confirm}
        onCancel={confirm.cancel}
      />
      </div>
    </>
  );
}
